import React, { Fragment, useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { FetchUsdBtcLedgerAmount, getAuthToken, getAuthUuid, fetchEscrowContractDetails } from "../utils/service";
import { showToast } from "../utils/library";
import { SITENAMEALIAS, PAYPAL_CLIENT_ID, WEBSERVICE } from "../utils/init";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

// ─────────────────────────────────────────────────────────────────
// Crypto master list (same coins as checkout)
// ─────────────────────────────────────────────────────────────────
const CRYPTO_MASTER_LIST = [
  { currency_code: "BTC",  currency_name: "Bitcoin",       logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/BTC.png",  network: ["NATIVE"] },
  { currency_code: "ETH",  currency_name: "Ethereum",      logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/ETH.png",  network: ["NATIVE"] },
  { currency_code: "BCH",  currency_name: "Bitcoin Cash",  logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/BCH.png",  network: ["NATIVE"] },
  { currency_code: "LTC",  currency_name: "Litecoin",      logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/LTC.png",  network: ["NATIVE"] },
  { currency_code: "HCX",  currency_name: "Hashcash Coin", logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/HCX.png",  network: ["NATIVE"] },
  { currency_code: "XRP",  currency_name: "Ripple",        logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/XRP.png",  network: ["NATIVE"] },
  { currency_code: "USDT", currency_name: "Tether",        logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/USDT.png", network: ["ERC", "TRC"] },
  { currency_code: "DOGE", currency_name: "Dogecoin",      logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/DOGE.png", network: ["NATIVE"] },
  { currency_code: "USDC", currency_name: "USDC", logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/USDC.png", network: ["ERC", "TRC"] },
];

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const STEPS = [
  { key: "details",    label: "Contract Details",     icon: "fa-file-contract" },
  { key: "approvers",  label: "Approvers",            icon: "fa-gavel" },
  { key: "conditions", label: "Release Conditions",   icon: "fa-list-ul" },
  { key: "payees",     label: "Payees",               icon: "fa-handshake" },
  { key: "review",     label: "Review",               icon: "fa-clipboard-check" },
];

const APPROVER_ROLES = ["Buyer", "Inspector", "Platform", "Legal", "Auditor", "Custom"];

// ─────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────
const CSS = `
.ecc-page { padding: 32px 24px 80px; max-width: 860px; overflow-x: hidden; }

.ecc-back-link { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; cursor: pointer; margin-bottom: 22px; color: var(--text-color); border: 1.5px solid var(--body-color-less2); background: var(--body-color-less); padding: 7px 14px; border-radius: 8px; font-family: inherit; opacity: .8; transition: opacity .15s, border-color .15s, background .15s; text-decoration: none; }
.ecc-back-link:hover { opacity: 1; border-color: var(--btn-color); background: color-mix(in srgb, var(--btn-color) 7%, transparent); color: var(--btn-color); text-decoration: none; }
.ecc-back-link .ecc-back-arrow { width: 22px; height: 22px; border-radius: 6px; background: color-mix(in srgb, var(--btn-color) 14%, transparent); color: var(--btn-color); display: inline-flex; align-items: center; justify-content: center; font-size: 11px; flex-shrink: 0; transition: background .15s; }
.ecc-back-link:hover .ecc-back-arrow { background: color-mix(in srgb, var(--btn-color) 24%, transparent); }

.ecc-hdr { margin-bottom: 28px; }
.ecc-title { font-size: 22px; font-weight: 700; letter-spacing: -.4px; color: var(--text-color); margin: 0 0 5px; }
.ecc-sub { font-size: 13px; color: var(--text-color); opacity: .6; margin: 0; }

/* ── Stepper ── */
.ecc-stepper { display: flex; align-items: flex-start; gap: 0; margin-bottom: 32px; overflow-x: auto; padding-bottom: 4px; }
.ecc-step-wrap { display: flex; align-items: center; flex: 1; min-width: 0; }
.ecc-step { display: flex; flex-direction: column; align-items: center; gap: 7px; cursor: default; min-width: 72px; }
.ecc-step-circle { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; transition: all .2s; border: 2px solid var(--body-color-less2); background: var(--body-color-less); color: var(--text-color); opacity: .45; }
.ecc-step.active .ecc-step-circle { border-color: var(--btn-color); background: color-mix(in srgb, var(--btn-color) 12%, transparent); color: var(--btn-color); opacity: 1; box-shadow: 0 0 0 4px color-mix(in srgb, var(--btn-color) 12%, transparent); }
.ecc-step.completed .ecc-step-circle { border-color: #10b981; background: rgba(16,185,129,.12); color: #10b981; opacity: 1; }
.ecc-step-label { font-size: 11px; font-weight: 600; color: var(--text-color); opacity: .45; text-align: center; white-space: nowrap; }
.ecc-step.active .ecc-step-label { color: var(--btn-color); opacity: 1; }
.ecc-step.completed .ecc-step-label { color: #10b981; opacity: 1; }
.ecc-step-connector { flex: 1; height: 2px; background: var(--body-color-less2); margin: 0 6px; margin-bottom: 22px; transition: background .2s; min-width: 16px; }
.ecc-step-connector.done { background: #10b981; }

/* ── Section card ── */
.ecc-section { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 12px; padding: 22px; margin-bottom: 20px; }
.ecc-section-hdr { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; padding-bottom: 12px; border-bottom: 1px solid var(--body-color-less2); }
.ecc-section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .8px; color: var(--text-color); opacity: .65; margin: 0; display: flex; align-items: center; gap: 8px; }

/* ── Participant card ── */
.ecc-participant { background: var(--body-color); border: 1px solid var(--body-color-less2); border-radius: 10px; padding: 18px; margin-bottom: 14px; position: relative; }
.ecc-participant:last-child { margin-bottom: 0; }
.ecc-participant-num { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--text-color); opacity: .4; margin-bottom: 14px; }
.ecc-participant-rm { position: absolute; top: 14px; right: 14px; width: 26px; height: 26px; border-radius: 6px; background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.2); color: #f87171; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 12px; transition: all .15s; }
.ecc-participant-rm:hover { background: rgba(239,68,68,.16); }

/* ── Form fields ── */
.ecc-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.ecc-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
.ecc-field { display: flex; flex-direction: column; gap: 5px; }
.ecc-label { font-size: 12px; font-weight: 600; color: var(--text-color); opacity: .8; display: flex; align-items: center; gap: 5px; }
.ecc-req { color: #ef4444; }
.ecc-opt { font-size: 10px; font-weight: 500; background: rgba(100,116,139,.13); color: var(--text-color); opacity: .65; border-radius: 4px; padding: 1px 6px; }
.ecc-input { width: 100%; height: 40px; background: var(--body-color-less); border: 1.5px solid var(--body-color-less2); border-radius: 8px; padding: 0 14px; color: var(--text-color); font-family: inherit; font-size: 14px; outline: none; transition: border-color .2s, box-shadow .2s; box-sizing: border-box; }
.ecc-input::placeholder { color: var(--text-color); opacity: .4; }
.ecc-input:focus { border-color: var(--btn-color); box-shadow: 0 0 0 3px color-mix(in srgb, var(--btn-color) 12%, transparent); }
.ecc-input.error { border-color: #ef4444; }
textarea.ecc-input { height: 88px; padding: 11px 14px; resize: vertical; line-height: 1.5; }
.ecc-sel { width: 100%; height: 40px; padding: 0 14px; background: var(--body-color-less); border: 1.5px solid var(--body-color-less2); border-radius: 8px; color: var(--text-color); font-family: inherit; font-size: 14px; outline: none; appearance: auto; cursor: pointer; box-sizing: border-box; transition: border-color .2s; }
.ecc-sel:focus { border-color: var(--btn-color); box-shadow: 0 0 0 3px color-mix(in srgb, var(--btn-color) 12%, transparent); }
.ecc-err { font-size: 11px; color: #ef4444; margin-top: 2px; }
.ecc-hint { font-size: 11px; color: var(--text-color); opacity: .5; margin-top: 2px; }

/* ── Terms rich editor ── */
.ecc-terms-editor { border: 1.5px solid var(--body-color-less2); border-radius: 8px; overflow: hidden; transition: border-color .2s, box-shadow .2s; }
.ecc-terms-editor:focus-within { border-color: var(--btn-color); box-shadow: 0 0 0 3px color-mix(in srgb, var(--btn-color) 12%, transparent); }
.ecc-terms-editor.error { border-color: #ef4444; }
.ecc-terms-toolbar { display: flex; align-items: center; gap: 3px; padding: 7px 10px; background: var(--body-color); border-bottom: 1px solid var(--body-color-less2); flex-wrap: wrap; }
.ecc-terms-fmt-btn { width: 30px; height: 28px; padding: 0; border-radius: 6px; border: 1px solid transparent; background: transparent; color: var(--text-color); font-family: inherit; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all .15s; flex-shrink: 0; }
.ecc-terms-fmt-btn:hover { background: color-mix(in srgb, var(--btn-color) 10%, transparent); border-color: color-mix(in srgb, var(--btn-color) 30%, transparent); color: var(--btn-color); }
.ecc-terms-fmt-btn.active { background: color-mix(in srgb, var(--btn-color) 16%, transparent); border-color: color-mix(in srgb, var(--btn-color) 40%, transparent); color: var(--btn-color); }
.ecc-terms-fmt-btn-wide { width: auto; padding: 0 10px; gap: 6px; font-size: 12px; font-weight: 600; }
.ecc-terms-divider { width: 1px; height: 18px; background: var(--body-color-less2); margin: 0 4px; flex-shrink: 0; }
.ecc-terms-content { min-height: 160px; background: var(--body-color-less); border: none; outline: none; padding: 11px 14px; color: var(--text-color); font-family: inherit; font-size: 14px; line-height: 1.75; box-sizing: border-box; word-break: break-word; }
.ecc-terms-content:empty:before { content: attr(data-placeholder); color: var(--text-color); opacity: .4; pointer-events: none; display: block; }
.ecc-terms-content ul, .ecc-terms-content ol { margin: 4px 0 4px 22px; padding: 0; }
.ecc-terms-content li { margin-bottom: 3px; }
.ecc-terms-content b, .ecc-terms-content strong, .ecc-terms-content [style*="font-weight"] { font-weight: 700 !important; }
.ecc-terms-content i, .ecc-terms-content em { font-style: italic !important; }
.ecc-terms-content u { text-decoration: underline !important; }
.ecc-terms-footer { display: flex; justify-content: flex-end; align-items: center; padding: 5px 12px; background: var(--body-color); border-top: 1px solid var(--body-color-less2); }
.ecc-terms-char { font-size: 11px; font-weight: 500; color: var(--text-color); opacity: .45; }
.ecc-terms-char.warn { color: #f59e0b; opacity: 1; font-weight: 600; }
.ecc-terms-char.over { color: #ef4444; opacity: 1; font-weight: 600; }
.ecc-terms-review { font-size: 13px; line-height: 1.65; padding: 14px 16px; background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 8px; color: var(--text-color); opacity: .7; }
.ecc-terms-review ul, .ecc-terms-review ol { margin: 4px 0 4px 20px; padding: 0; }
.ecc-terms-review li { margin-bottom: 3px; }
.ecc-terms-review b, .ecc-terms-review strong { font-weight: 700; }
.ecc-terms-review i, .ecc-terms-review em { font-style: italic; }
.ecc-terms-review u { text-decoration: underline; }

/* ── Amount row ── */
.ecc-amount-row { display: flex; gap: 0; }
.ecc-amount-row .ecc-input { border-radius: 8px; }
.ecc-amount-row .ecc-input:focus { border-color: var(--btn-color); }

/* ── Add button ── */
.ecc-btn-add { height: 34px; padding: 0 14px; border-radius: 8px; background: color-mix(in srgb, var(--btn-color) 10%, transparent); border: 1.5px dashed color-mix(in srgb, var(--btn-color) 35%, transparent); color: var(--btn-color); font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: all .15s; }
.ecc-btn-add:hover { background: color-mix(in srgb, var(--btn-color) 18%, transparent); }

/* ── Approval type toggle ── */
.ecc-radio-group { display: flex; gap: 12px; flex-wrap: wrap; }
.ecc-radio-opt { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: var(--text-color); opacity: .8; padding: 8px 14px; border-radius: 8px; border: 1.5px solid var(--body-color-less2); transition: all .15s; user-select: none; }
.ecc-radio-opt:has(input:checked) { border-color: var(--btn-color); background: color-mix(in srgb, var(--btn-color) 8%, transparent); opacity: 1; }
.ecc-radio-opt input { accent-color: var(--btn-color); width: 14px; height: 14px; }

/* ── Condition card ── */
.ecc-condition { background: var(--body-color); border: 1px solid var(--body-color-less2); border-radius: 10px; padding: 18px; margin-bottom: 14px; position: relative; }
.ecc-condition:last-child { margin-bottom: 0; }
.ecc-condition-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: color-mix(in srgb, var(--btn-color) 12%, transparent); color: var(--btn-color); font-size: 11px; font-weight: 700; margin-bottom: 12px; }

/* ── Amount / Percentage type toggle ── */
.ecc-type-toggle { display: inline-flex; border: 1.5px solid var(--body-color-less2); border-radius: 8px; overflow: hidden; margin-bottom: 14px; }
.ecc-type-btn { padding: 6px 16px; font-size: 12px; font-weight: 600; background: transparent; border: none; color: var(--text-color); opacity: .6; cursor: pointer; font-family: inherit; transition: all .15s; white-space: nowrap; }
.ecc-type-btn.active { background: var(--btn-color); color: #fff; opacity: 1; }
.ecc-pct-row { display: flex; gap: 0; }
.ecc-pct-row .ecc-input { border-radius: 8px 0 0 8px; flex: 1; }
.ecc-pct-suffix { display: flex; align-items: center; justify-content: center; height: 40px; padding: 0 14px; background: var(--body-color-less2); border: 1.5px solid var(--body-color-less2); border-left: none; border-radius: 0 8px 8px 0; font-size: 14px; font-weight: 700; color: var(--text-color); flex-shrink: 0; }

/* ── Total bar ── */
.ecc-total-bar { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; background: color-mix(in srgb, var(--btn-color) 7%, transparent); border: 1px solid color-mix(in srgb, var(--btn-color) 22%, transparent); border-radius: 10px; margin-top: 18px; }
.ecc-total-label { font-size: 13px; font-weight: 600; color: var(--text-color); opacity: .8; }
.ecc-total-value { font-size: 18px; font-weight: 700; color: var(--btn-color); }

/* ── Review ── */
.ecc-review-section { margin-bottom: 20px; }
.ecc-review-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .7px; color: var(--text-color); opacity: .5; margin: 0 0 12px; }
.ecc-review-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.ecc-review-card { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 10px; padding: 14px 16px; }
.ecc-review-card-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--text-color); opacity: .45; margin-bottom: 4px; }
.ecc-review-card-value { font-size: 13px; font-weight: 600; color: var(--text-color); }
.ecc-review-card-sub { font-size: 11px; color: var(--text-color); opacity: .55; margin-top: 2px; }
.ecc-review-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.ecc-review-list-item { display: flex; align-items: flex-start; gap: 9px; font-size: 13px; color: var(--text-color); opacity: .8; line-height: 1.45; }
.ecc-review-list-item-num { min-width: 20px; height: 20px; border-radius: 50%; background: color-mix(in srgb, var(--btn-color) 12%, transparent); color: var(--btn-color); font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.ecc-review-pill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; background: rgba(100,116,139,.13); color: var(--text-color); opacity: .7; }
.ecc-review-amount { font-size: 24px; font-weight: 700; color: var(--btn-color); }

/* ── Nav buttons ── */
.ecc-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 20px; border-top: 1px solid var(--body-color-less2); margin-top: 4px; }
.ecc-btn-back { height: 40px; padding: 0 20px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .75; font-family: inherit; font-size: 14px; font-weight: 500; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; gap: 7px; }
.ecc-btn-back:hover { opacity: 1; border-color: var(--text-color); }
.ecc-btn-next { height: 40px; padding: 0 24px; border-radius: 8px; border: none; background: var(--btn-color); color: #fff; font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 0 14px color-mix(in srgb, var(--btn-color) 30%, transparent); transition: filter .15s, transform .15s; display: inline-flex; align-items: center; gap: 7px; }
.ecc-btn-next:hover { filter: brightness(1.1); transform: scale(.98); }
.ecc-btn-next:disabled { opacity: .5; cursor: not-allowed; transform: none; }
.ecc-btn-fund { height: 40px; padding: 0 28px; border-radius: 8px; border: none; background: #10b981; color: #fff; font-family: inherit; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 0 16px rgba(16,185,129,.3); transition: filter .15s, transform .15s; display: inline-flex; align-items: center; gap: 8px; }
.ecc-btn-fund:hover { filter: brightness(1.08); transform: scale(.98); }
.ecc-btn-draft { height: 40px; padding: 0 20px; border-radius: 8px; border: 1.5px solid var(--body-color-less2); background: transparent; color: var(--text-color); font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; gap: 7px; opacity: .8; }
.ecc-btn-draft:hover { opacity: 1; border-color: var(--btn-color); color: var(--btn-color); background: color-mix(in srgb, var(--btn-color) 6%, transparent); }

/* ── Approver picker (inside conditions) ── */
.ecc-approver-pick-list { display: flex; flex-direction: column; gap: 7px; }
.ecc-approver-pick-opt { display: flex; align-items: center; gap: 10px; cursor: pointer; font-size: 13px; color: var(--text-color); padding: 10px 13px; border-radius: 8px; border: 1.5px solid var(--body-color-less2); transition: all .15s; user-select: none; }
.ecc-approver-pick-opt:has(input:checked) { border-color: var(--btn-color); background: color-mix(in srgb, var(--btn-color) 8%, transparent); }
.ecc-approver-pick-opt input[type="checkbox"] { accent-color: var(--btn-color); width: 15px; height: 15px; cursor: pointer; flex-shrink: 0; }
.ecc-approver-pick-name { font-weight: 600; }
.ecc-approver-pick-role { font-size: 11px; opacity: .55; margin-left: 5px; padding: 1px 6px; border-radius: 4px; background: rgba(100,116,139,.1); }
.ecc-approver-pick-email { font-size: 11px; opacity: .5; margin-left: auto; }
.ecc-approver-pick-empty { font-size: 12px; color: var(--text-color); opacity: .45; padding: 10px 13px; border-radius: 8px; border: 1.5px dashed var(--body-color-less2); text-align: center; }

/* ── Approval threshold list ── */
.ecc-threshold-list { display: flex; flex-direction: column; gap: 8px; }
.ecc-threshold-opt { display: flex; align-items: center; gap: 10px; cursor: pointer; font-size: 13px; color: var(--text-color); padding: 10px 14px; border-radius: 8px; border: 1.5px solid var(--body-color-less2); transition: all .15s; user-select: none; }
.ecc-threshold-opt:has(input:checked) { border-color: var(--btn-color); background: color-mix(in srgb, var(--btn-color) 8%, transparent); }
.ecc-threshold-opt input[type="radio"] { accent-color: var(--btn-color); width: 15px; height: 15px; cursor: pointer; flex-shrink: 0; }
.ecc-threshold-opt-label { flex: 1; line-height: 1.4; }
.ecc-threshold-badge { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 10px; background: color-mix(in srgb, var(--btn-color) 12%, transparent); color: var(--btn-color); flex-shrink: 0; }

/* ── Fund step payment method cards ── */
.ecc-fund-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.ecc-fund-card { position: relative; display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 28px 20px 22px; border-radius: 14px; border: 2px solid var(--body-color-less2); background: var(--body-color); cursor: pointer; transition: all .18s; user-select: none; text-align: center; }
.ecc-fund-card:hover { border-color: var(--ecc-fund-color, var(--btn-color)); background: color-mix(in srgb, var(--ecc-fund-color, var(--btn-color)) 5%, var(--body-color)); }
.ecc-fund-card.selected { border-color: var(--ecc-fund-color, var(--btn-color)); background: color-mix(in srgb, var(--ecc-fund-color, var(--btn-color)) 8%, var(--body-color)); box-shadow: 0 0 0 4px color-mix(in srgb, var(--ecc-fund-color, var(--btn-color)) 15%, transparent); }
.ecc-fund-card input[type="radio"] { position: absolute; opacity: 0; pointer-events: none; }
.ecc-fund-card-icon { width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 24px; background: color-mix(in srgb, var(--ecc-fund-color, var(--btn-color)) 14%, transparent); color: var(--ecc-fund-color, var(--btn-color)); transition: background .18s; }
.ecc-fund-card.selected .ecc-fund-card-icon { background: color-mix(in srgb, var(--ecc-fund-color, var(--btn-color)) 22%, transparent); }
.ecc-fund-card-title { font-size: 15px; font-weight: 700; color: var(--text-color); }
.ecc-fund-card-desc { font-size: 12px; color: var(--text-color); opacity: .55; line-height: 1.5; }
.ecc-fund-card-check { position: absolute; top: 12px; right: 12px; width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--body-color-less2); background: transparent; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #fff; transition: all .18s; }
.ecc-fund-card.selected .ecc-fund-card-check { border-color: var(--ecc-fund-color, var(--btn-color)); background: var(--ecc-fund-color, var(--btn-color)); }
@media (max-width: 700px) { .ecc-fund-grid { grid-template-columns: 1fr; } }

/* ── Fund amount + PayPal panel ── */
.ecc-fund-amount-box { margin-top: 22px; padding: 18px 20px; background: var(--body-color); border: 1px solid var(--body-color-less2); border-radius: 12px; }
.ecc-fund-amount-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .7px; color: var(--text-color); opacity: .5; margin-bottom: 8px; }
.ecc-fund-amount-input-wrap { position: relative; }
.ecc-fund-amount-prefix { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); font-size: 14px; font-weight: 700; color: var(--text-color); opacity: .5; pointer-events: none; }
.ecc-fund-amount-field { width: 100%; height: 46px; background: var(--body-color-less); border: 1.5px solid var(--body-color-less2); border-radius: 8px; padding: 0 14px 0 32px; color: var(--text-color); font-family: inherit; font-size: 18px; font-weight: 700; outline: none; box-sizing: border-box; }
.ecc-fund-amount-currency { position: absolute; right: 13px; top: 50%; transform: translateY(-50%); font-size: 12px; font-weight: 700; color: var(--text-color); opacity: .45; pointer-events: none; }
.ecc-paypal-panel { margin-top: 18px; }
.ecc-paypal-divider { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.ecc-paypal-divider-line { flex: 1; height: 1px; background: var(--body-color-less2); }
.ecc-paypal-divider-text { font-size: 11px; font-weight: 600; color: var(--text-color); opacity: .4; white-space: nowrap; }

/* ── Crypto address panel ── */
.ecc-crypto-panel { margin-top: 18px; }
.ecc-crypto-list { border: 1.5px solid var(--body-color-less2); border-radius: 10px; overflow: hidden; max-height: 220px; overflow-y: auto; }
.ecc-crypto-row { display: flex; align-items: center; gap: 10px; padding: 9px 12px; cursor: pointer; border-bottom: 1px solid var(--body-color-less2); transition: background .12s; box-sizing: border-box; }
.ecc-crypto-row:last-child { border-bottom: none; }
.ecc-crypto-row:hover { background: color-mix(in srgb, var(--btn-color) 5%, transparent); }
.ecc-crypto-row.selected { background: color-mix(in srgb, #2563eb 8%, transparent); }
.ecc-crypto-logo { width: 26px; height: 26px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.ecc-crypto-code { font-size: 12px; font-weight: 700; min-width: 44px; color: var(--text-color); }
.ecc-crypto-row.selected .ecc-crypto-code { color: #2563eb; }
.ecc-crypto-name { font-size: 11px; color: var(--text-color); opacity: .5; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ecc-crypto-check { width: 16px; height: 16px; border-radius: 50%; border: 1.5px solid var(--body-color-less2); background: transparent; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .15s; }
.ecc-crypto-row.selected .ecc-crypto-check { border-color: #2563eb; background: #2563eb; }
.ecc-crypto-equiv { margin-top: 14px; padding: 14px 16px; background: color-mix(in srgb, #2563eb 8%, transparent); border: 1px solid color-mix(in srgb, #2563eb 25%, transparent); border-radius: 10px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.ecc-crypto-equiv-label { font-size: 12px; font-weight: 600; color: var(--text-color); opacity: .65; }
.ecc-crypto-equiv-value { font-size: 17px; font-weight: 700; color: #2563eb; white-space: nowrap; }
@keyframes ecc-spin { to { transform: rotate(360deg); } }
.ecc-crypto-spinner { width: 14px; height: 14px; border-radius: 50%; border: 2px solid color-mix(in srgb, #2563eb 25%, transparent); border-top-color: #2563eb; display: inline-block; animation: ecc-spin .7s linear infinite; }
/* ── Bank transfer panel ── */
.ecc-bank-panel { margin-top: 18px; }
.ecc-bank-notice { padding: 13px 16px; background: color-mix(in srgb, #2563eb 7%, transparent); border: 1px solid color-mix(in srgb, #2563eb 20%, transparent); border-radius: 10px; margin-bottom: 14px; }
.ecc-bank-notice-title { font-size: 12px; font-weight: 700; color: #2563eb; margin: 0 0 8px; display: flex; align-items: center; gap: 6px; }
.ecc-bank-notice-list { margin: 0; padding: 0 0 0 16px; display: flex; flex-direction: column; gap: 5px; }
.ecc-bank-notice-list li { font-size: 12px; color: var(--text-color); opacity: .7; line-height: 1.5; }
.ecc-bank-details-title { font-size: 12px; font-weight: 700; color: var(--text-color); opacity: .65; margin: 0 0 10px; display: flex; align-items: center; gap: 6px; }
.ecc-bank-table { width: 100%; border-collapse: collapse; border: 1.5px solid var(--body-color-less2); border-radius: 10px; overflow: hidden; }
.ecc-bank-table tr { border-bottom: 1px solid var(--body-color-less2); }
.ecc-bank-table tr:last-child { border-bottom: none; }
.ecc-bank-table td { padding: 9px 14px; font-size: 13px; line-height: 1.45; }
.ecc-bank-table td:first-child { font-weight: 600; color: var(--text-color); opacity: .55; width: 42%; white-space: nowrap; }
.ecc-bank-table td:last-child { font-weight: 700; color: var(--text-color); word-break: break-all; }
.ecc-network-section { margin-top: 12px; }
.ecc-network-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .7px; color: var(--text-color); opacity: .5; margin-bottom: 8px; }
.ecc-network-options { display: flex; gap: 10px; }
.ecc-network-opt { flex: 1; display: flex; align-items: center; gap: 9px; padding: 10px 14px; border-radius: 9px; border: 1.5px solid var(--body-color-less2); cursor: pointer; user-select: none; transition: all .15s; }
.ecc-network-opt:has(input:checked) { border-color: #2563eb; background: color-mix(in srgb, #2563eb 8%, transparent); }
.ecc-network-opt input[type="radio"] { accent-color: #2563eb; width: 14px; height: 14px; cursor: pointer; flex-shrink: 0; }
.ecc-network-opt-name { font-size: 13px; font-weight: 700; color: var(--text-color); }
.ecc-network-opt-desc { font-size: 11px; color: var(--text-color); opacity: .45; margin-top: 1px; }

/* ── Confirm ── */
.ecc-confirm-bg { position: fixed; inset: 0; background: rgba(0,0,0,.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.ecc-confirm-bg.open { opacity: 1; pointer-events: all; }
.ecc-confirm-box { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 14px; padding: 28px; width: 400px; max-width: 92vw; transform: translateY(12px); transition: transform .2s; }
.ecc-confirm-bg.open .ecc-confirm-box { transform: translateY(0); }
.ecc-confirm-icon { width: 48px; height: 48px; border-radius: 50%; background: rgba(16,185,129,.12); border: 1.5px solid rgba(16,185,129,.3); display: flex; align-items: center; justify-content: center; font-size: 20px; color: #10b981; margin-bottom: 16px; }
.ecc-confirm-title { font-size: 17px; font-weight: 700; color: var(--text-color); margin: 0 0 8px; }
.ecc-confirm-msg { font-size: 13px; color: var(--text-color); opacity: .7; margin: 0 0 22px; line-height: 1.6; }
.ecc-confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }
.ecc-confirm-cancel-btn { height: 38px; padding: 0 18px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .8; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s; }
.ecc-confirm-cancel-btn:hover { opacity: 1; border-color: var(--text-color); }
.ecc-confirm-ok-btn { height: 38px; padding: 0 22px; border-radius: 8px; border: none; background: #10b981; color: #fff; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 0 12px rgba(16,185,129,.3); transition: filter .15s; }
.ecc-confirm-ok-btn:hover { filter: brightness(1.08); }

/* ── File upload ── */
.ecc-file-label { display: flex; align-items: center; gap: 8px; padding: 9px 14px; background: var(--body-color-less); border: 1.5px dashed var(--body-color-less2); border-radius: 8px; cursor: pointer; font-size: 13px; color: var(--text-color); opacity: .7; transition: all .15s; }
.ecc-file-label:hover { border-color: var(--btn-color); opacity: 1; }
.ecc-file-input { display: none; }
.ecc-file-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.ecc-file-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; background: color-mix(in srgb, var(--btn-color) 10%, transparent); border: 1px solid color-mix(in srgb, var(--btn-color) 25%, transparent); border-radius: 20px; font-size: 11px; font-weight: 600; color: var(--btn-color); max-width: 200px; }
.ecc-file-chip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ecc-file-chip-rm { cursor: pointer; opacity: .6; flex-shrink: 0; line-height: 1; background: none; border: none; color: inherit; padding: 0; font-size: 10px; }
.ecc-file-chip-rm:hover { opacity: 1; }

/* ── Success ── */
.ecc-success { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; text-align: center; gap: 14px; }
.ecc-success-icon { width: 64px; height: 64px; border-radius: 50%; background: rgba(16,185,129,.12); border: 2px solid rgba(16,185,129,.3); display: flex; align-items: center; justify-content: center; font-size: 26px; color: #10b981; }
.ecc-success-title { font-size: 20px; font-weight: 700; color: var(--text-color); margin: 0; }
.ecc-success-sub { font-size: 13px; color: var(--text-color); opacity: .6; margin: 0; max-width: 380px; line-height: 1.55; }

@keyframes ecc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.ecc-view-in { animation: ecc-fade-up .2s ease; }
.ecc-pane-in { animation: ecc-fade-up .18s ease; }

@media (max-width: 700px) {
  .ecc-page { padding: 16px 10px 80px; }
  .ecc-grid-2, .ecc-grid-3 { grid-template-columns: 1fr; }
  .ecc-review-grid { grid-template-columns: 1fr; }
  .ecc-step-label { display: none; }
  .ecc-footer { flex-wrap: wrap; gap: 10px; }
}
`;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const empty = (v) => !v || !String(v).trim();

function calcTotal(conditions) {
  return conditions.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
}

function fmtMoney(n, currency = "USD") {
  return `${currency} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function isValidWalletAddress(address, currency) {
  const addr = address.trim();
  switch (currency) {
    case "BTC":
      return /^(bc1[ac-hj-np-z02-9]{6,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(addr);
    case "ETH":
    case "HCX":
      return /^0x[0-9a-fA-F]{40}$/.test(addr);
    case "BCH":
      return /^((bitcoincash:)?(q|p)[a-z0-9]{41}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(addr);
    case "LTC":
      return /^([LM][a-km-zA-HJ-NP-Z1-9]{26,33}|ltc1[a-z0-9]{6,87})$/.test(addr);
    case "XRP":
      return /^r[0-9a-zA-Z]{24,34}$/.test(addr);
    case "DOGE":
      return /^D[0-9A-Za-z]{32,33}$/.test(addr);
    case "USDT":
    case "USDC":
      // Accept both ERC-20 (Ethereum) and TRC-20 (Tron) formats
      return /^0x[0-9a-fA-F]{40}$/.test(addr) || /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(addr);
    default:
      return true;
  }
}

// ─────────────────────────────────────────────────────────────────
// Field wrapper
// ─────────────────────────────────────────────────────────────────
function Field({ label, req, opt, hint, error, children, full }) {
  return (
    <div className="ecc-field" style={full ? { gridColumn: "1 / -1" } : {}}>
      <label className="ecc-label">
        {label}
        {req && <span className="ecc-req">*</span>}
        {opt && <span className="ecc-opt">Optional</span>}
      </label>
      {children}
      {hint && !error && <span className="ecc-hint">{hint}</span>}
      {error && <span className="ecc-err">{error}</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 1 — Contract Details
// ─────────────────────────────────────────────────────────────────
function StepDetails({ contractName, setContractName, contractDescription, setContractDescription, errors }) {
  return (
    <div className="ecc-pane-in">
      <div className="ecc-section">
        <div className="ecc-section-hdr">
          <p className="ecc-section-title">
            <i className="fas fa-file-contract" style={{ fontSize: 11 }} /> Contract Details
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Field label="Contract Name" req error={errors.contractName}>
            <input
              className={`ecc-input${errors.contractName ? " error" : ""}`}
              placeholder="e.g. Software Development Agreement"
              value={contractName}
              onChange={(e) => setContractName(e.target.value)}
            />
          </Field>
          <Field label="Contract Description" opt hint={!errors.contractDescription ? "Briefly describe the purpose or scope of this escrow contract" : undefined} error={errors.contractDescription}>
            <textarea
              className={`ecc-input${errors.contractDescription ? " error" : ""}`}
              placeholder="e.g. This escrow covers the development and delivery of a mobile application…"
              value={contractDescription}
              onChange={(e) => setContractDescription(e.target.value)}
              style={{ height: 110 }}
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 1 — Approvers
// ─────────────────────────────────────────────────────────────────
function StepApprovers({ approvers, setApprovers, approvalType, setApprovalType, errors }) {
  const add = () =>
    setApprovers((a) => [...a, { role: "Inspector", name: "", email: "" }]);
  const rm = (i) => setApprovers((a) => a.filter((_, idx) => idx !== i));
  const upd = (i, key, val) =>
    setApprovers((a) => a.map((item, idx) => (idx === i ? { ...item, [key]: val } : item)));

  const n = approvers.length;

  // If approvers are removed and the current threshold is no longer valid, reset to "all"
  useEffect(() => {
    if (approvalType !== "all") {
      const t = parseInt(approvalType, 10);
      if (isNaN(t) || t >= n) {
        setApprovalType("all");
      }
    }
  }, [n]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ecc-pane-in">
      <div className="ecc-section">
        <div className="ecc-section-hdr">
          <p className="ecc-section-title">
            <i className="fas fa-gavel" style={{ fontSize: 11 }} /> Approvers
          </p>
        </div>

        {n === 0 && (
          <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .5, marginBottom: 16 }}>
            No approvers added. Escrow will release when payer confirms delivery.
          </p>
        )}

        {approvers.map((approver, i) => (
          <div className="ecc-participant" key={i}>
            <div className="ecc-participant-num">Approver {i + 1}</div>
            <button className="ecc-participant-rm" onClick={() => rm(i)}>
              <i className="fas fa-times" />
            </button>
            <div className="ecc-grid-3">
              <Field label="Role" req error={errors[`approver_${i}_role`]}>
                <select
                  className={`ecc-sel${errors[`approver_${i}_role`] ? " error" : ""}`}
                  value={approver.role}
                  onChange={(e) => upd(i, "role", e.target.value)}
                >
                  {APPROVER_ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </Field>
              <Field label="Full Name" req error={errors[`approver_${i}_name`]}>
                <input
                  className={`ecc-input${errors[`approver_${i}_name`] ? " error" : ""}`}
                  placeholder="e.g. Sarah Lee"
                  value={approver.name}
                  onChange={(e) => upd(i, "name", e.target.value)}
                />
              </Field>
              <Field label="Email Address" req error={errors[`approver_${i}_email`]}>
                <input
                  className={`ecc-input${errors[`approver_${i}_email`] ? " error" : ""}`}
                  placeholder="sarah@company.com"
                  value={approver.email}
                  onChange={(e) => upd(i, "email", e.target.value)}
                />
              </Field>
            </div>
          </div>
        ))}
        <button className="ecc-btn-add" onClick={add} style={{ marginTop: 4 }}>
          <i className="fas fa-plus" style={{ fontSize: 11 }} /> Add Approver
        </button>
      </div>

      {/* Approval Threshold — only visible when at least one approver is present */}
      {n > 0 && (
        <div className="ecc-section">
          <div className="ecc-section-hdr">
            <p className="ecc-section-title">
              <i className="fas fa-tasks" style={{ fontSize: 11 }} /> Approval Threshold
            </p>
          </div>
          <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .6, margin: "0 0 14px", lineHeight: 1.5 }}>
            How many of the {n} approver{n !== 1 ? "s" : ""} must approve before funds are released?
          </p>
          <div className="ecc-threshold-list">
            {/* All approvers option (default) */}
            <label className="ecc-threshold-opt">
              <input
                type="radio"
                name="approvalThreshold"
                value="all"
                checked={approvalType === "all"}
                onChange={() => setApprovalType("all")}
              />
              <span className="ecc-threshold-opt-label">
                All approvers are required to approve
              </span>
              <span className="ecc-threshold-badge">default</span>
            </label>

            {/* k out of n options, from n-1 down to 1 */}
            {Array.from({ length: n - 1 }, (_, idx) => n - 1 - idx).map((k) => (
              <label className="ecc-threshold-opt" key={k}>
                <input
                  type="radio"
                  name="approvalThreshold"
                  value={String(k)}
                  checked={approvalType === String(k)}
                  onChange={() => setApprovalType(String(k))}
                />
                <span className="ecc-threshold-opt-label">
                  <strong>{k}</strong> out of <strong>{n}</strong> approvers are required to approve
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Terms Editor — formatting toolbar + char count
// ─────────────────────────────────────────────────────────────────
function TermsEditor({ value, onChange, hasError }) {
  const editorRef    = React.useRef(null);
  const savedRange   = React.useRef(null);   // persisted selection range
  const [charLen, setCharLen] = React.useState(0);
  const [fmt, setFmt] = React.useState({ bold: false, italic: false, underline: false, list: false });
  const MAX = 5000;

  // Sync external value (e.g. draft load) only while the editor is not focused
  React.useEffect(() => {
    const el = editorRef.current;
    if (!el || document.activeElement === el) return;
    el.innerHTML = value || "";
    setCharLen(el.textContent.length);
  }, [value]);

  // Save the current Selection range so toolbar clicks can restore it
  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // Restore the saved range into the editor before applying a command
  const restoreRange = () => {
    const el  = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || !savedRange.current) return;
    el.focus();
    sel.removeAllRanges();
    sel.addRange(savedRange.current);
  };

  const syncFmt = () => {
    setFmt({
      bold:      document.queryCommandState("bold"),
      italic:    document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      list:      document.queryCommandState("insertUnorderedList"),
    });
  };

  const exec = (cmd) => {
    restoreRange();                              // put selection back before formatting
    document.execCommand(cmd, false, null);
    const el = editorRef.current;
    if (el) { onChange(el.innerHTML); setCharLen(el.textContent.length); }
    syncFmt();
  };

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;
    onChange(el.innerHTML);
    setCharLen(el.textContent.length);
  };

  const charClass = "ecc-terms-char" + (charLen > MAX ? " over" : charLen > MAX * 0.9 ? " warn" : "");

  return (
    <div className={`ecc-terms-editor${hasError ? " error" : ""}`}>
      <div className="ecc-terms-toolbar">
        <button
          type="button"
          className={`ecc-terms-fmt-btn${fmt.bold ? " active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => exec("bold")}
          title="Bold (Ctrl+B)"
        >
          <strong style={{ fontSize: 14, lineHeight: 1 }}>B</strong>
        </button>
        <button
          type="button"
          className={`ecc-terms-fmt-btn${fmt.italic ? " active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => exec("italic")}
          title="Italic (Ctrl+I)"
        >
          <em style={{ fontStyle: "italic", fontFamily: "Georgia, serif", fontSize: 14, lineHeight: 1 }}>I</em>
        </button>
        <button
          type="button"
          className={`ecc-terms-fmt-btn${fmt.underline ? " active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => exec("underline")}
          title="Underline (Ctrl+U)"
        >
          <span style={{ textDecoration: "underline", fontSize: 13, fontWeight: 600, lineHeight: 1 }}>U</span>
        </button>

        <div className="ecc-terms-divider" />

        <button
          type="button"
          className={`ecc-terms-fmt-btn ecc-terms-fmt-btn-wide${fmt.list ? " active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => exec("insertUnorderedList")}
          title="Bullet list"
        >
          <i className="fas fa-list-ul" style={{ fontSize: 11 }} />
          Bullet List
        </button>
      </div>

      <div
        ref={editorRef}
        className="ecc-terms-content"
        contentEditable
        suppressContentEditableWarning
        data-placeholder="e.g. Disputes must be raised within 7 days of milestone delivery…"
        onInput={handleInput}
        onKeyUp={() => { saveRange(); syncFmt(); }}
        onMouseUp={() => { saveRange(); syncFmt(); }}
        onSelect={saveRange}
        onFocus={syncFmt}
      />

      <div className="ecc-terms-footer">
        <span className={charClass}>{charLen.toLocaleString()} / {MAX.toLocaleString()}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 3 — Release Conditions
// ─────────────────────────────────────────────────────────────────
function StepConditions({
  conditions, setConditions,
  approvers,
  releaseApprovers, setReleaseApprovers,
  releaseType, setReleaseType,
  releaseDate, setReleaseDate,
  terms, setTerms, errors,
}) {
  const add = () => {
    if (conditions.length >= 10) return;
    setConditions((c) => [...c, { label: "", deliverables: "", documents: [], existingDocuments: [], completionDate: "", percentage: "", amountType: "percentage", milestoneReleaseType: "on_approval", milestoneReleaseDate: "", milestoneApprovers: [] }]);
  };

  const toggleReleaseApprover = (ai) => {
    setReleaseApprovers((prev) =>
      prev.includes(ai) ? prev.filter((x) => x !== ai) : [...prev, ai]
    );
  };

  const toggleMilestoneApprover = (ci, ai) => {
    const curr = conditions[ci].milestoneApprovers || [];
    upd(ci, "milestoneApprovers", curr.includes(ai) ? curr.filter((x) => x !== ai) : [...curr, ai]);
  };
  const rm = (i) => setConditions((c) => c.filter((_, idx) => idx !== i));
  const upd = (i, key, val) =>
    setConditions((c) => c.map((item, idx) => (idx === i ? { ...item, [key]: val } : item)));

  const milestoneTotal = calcTotal(conditions);

  return (
    <div className="ecc-pane-in">
      {/* Release type toggle */}
      <div className="ecc-section">
        <div className="ecc-section-hdr">
          <p className="ecc-section-title">
            <i className="fas fa-unlock-alt" style={{ fontSize: 11 }} /> Release Type
          </p>
        </div>
        <div className="ecc-radio-group">
          <label className="ecc-radio-opt">
            <input
              type="radio"
              name="releaseType"
              value="on_approval"
              checked={releaseType === "on_approval"}
              onChange={() => setReleaseType("on_approval")}
            />
            On Approval
          </label>
          <label className="ecc-radio-opt">
            <input
              type="radio"
              name="releaseType"
              value="date"
              checked={releaseType === "date"}
              onChange={() => setReleaseType("date")}
            />
            Release Date
          </label>
          <label className="ecc-radio-opt">
            <input
              type="radio"
              name="releaseType"
              value="milestone"
              checked={releaseType === "milestone"}
              onChange={() => setReleaseType("milestone")}
            />
            Milestone-based
          </label>
        </div>
      </div>

      {/* ── On Approval view ── */}
      {releaseType === "on_approval" && (
        <div className="ecc-section">
          <div className="ecc-section-hdr">
            <p className="ecc-section-title">
              <i className="fas fa-check-circle" style={{ fontSize: 11 }} /> On Approval
            </p>
          </div>
          <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .65, margin: "0 0 18px", lineHeight: 1.6 }}>
            Funds will be released automatically once all required approvers confirm delivery.
          </p>

          {/* Approver selection */}
          <div style={{ marginBottom: 20 }}>
            <div className="ecc-label" style={{ marginBottom: 10 }}>
              <i className="fas fa-gavel" style={{ fontSize: 11, opacity: .7 }} /> Select Approvers
            </div>
            {approvers.length === 0 ? (
              <div className="ecc-approver-pick-empty">
                No approvers added yet — go back to the Approvers step to add them.
              </div>
            ) : (
              <div className="ecc-approver-pick-list">
                {approvers.map((a, ai) => (
                  <label className="ecc-approver-pick-opt" key={ai}>
                    <input
                      type="checkbox"
                      checked={releaseApprovers.includes(ai)}
                      onChange={() => toggleReleaseApprover(ai)}
                    />
                    <span>
                      <span className="ecc-approver-pick-name">{a.name || `Approver ${ai + 1}`}</span>
                      {a.role && <span className="ecc-approver-pick-role">{a.role}</span>}
                    </span>
                    {a.email && <span className="ecc-approver-pick-email">{a.email}</span>}
                  </label>
                ))}
              </div>
            )}
            {errors.releaseApprovers && (
              <p className="ecc-err">{errors.releaseApprovers}</p>
            )}
          </div>

        </div>
      )}

      {/* ── Milestone view ── */}
      {releaseType === "milestone" && (
        <div className="ecc-section">
          <div className="ecc-section-hdr">
            <p className="ecc-section-title">
              <i className="fas fa-list-ul" style={{ fontSize: 11 }} /> Milestones
              <span style={{ marginLeft: 8, fontWeight: 500, fontSize: 11, opacity: .55 }}>
                {conditions.length} / 10
              </span>
            </p>
          </div>

          {conditions.map((cond, i) => (
            <div className="ecc-condition" key={i}>
              <div className="ecc-condition-num">{i + 1}</div>
              {conditions.length > 1 && (
                <button className="ecc-participant-rm" onClick={() => rm(i)}>
                  <i className="fas fa-times" />
                </button>
              )}
              {/* Title — full width */}
              <Field label="Milestone Title" req error={errors[`cond_${i}_label`]}>
                <input
                  className={`ecc-input${errors[`cond_${i}_label`] ? " error" : ""}`}
                  placeholder="e.g. Wireframes Delivered"
                  value={cond.label}
                  onChange={(e) => upd(i, "label", e.target.value)}
                />
              </Field>

              {/* Percentage of total escrow for this milestone */}
              <div style={{ marginTop: 14 }}>
                <Field label="Milestone Percentage" req error={errors[`cond_${i}_pct`]} hint="What % of the total escrow amount releases at this milestone (e.g. 25%)">
                  <div className="ecc-pct-row" style={{ maxWidth: 200 }}>
                    <input
                      className={`ecc-input${errors[`cond_${i}_pct`] ? " error" : ""}`}
                      type="number"
                      min="1"
                      max="100"
                      placeholder="e.g. 25"
                      value={cond.percentage || ""}
                      onChange={(e) => upd(i, "percentage", e.target.value)}
                    />
                    <span className="ecc-pct-suffix">%</span>
                  </div>
                </Field>
              </div>

              {/* Deliverable */}
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--body-color-less2)" }}>
                <div className="ecc-label" style={{ marginBottom: 10 }}>
                  <i className="fas fa-tasks" style={{ fontSize: 11, opacity: .7 }} /> Deliverable
                </div>
                <Field label="(a) Define Deliverables" req error={errors[`cond_${i}_deliverables`]}>
                  <textarea
                    className={`ecc-input${errors[`cond_${i}_deliverables`] ? " error" : ""}`}
                    placeholder="Describe what must be completed and delivered for this milestone…"
                    value={cond.deliverables}
                    onChange={(e) => upd(i, "deliverables", e.target.value)}
                    style={{ height: 72 }}
                  />
                </Field>
                <div style={{ marginTop: 12 }}>
                  <Field label="(b) Upload Documents" opt>
                    {cond.existingDocuments?.length > 0 && (
                      <div className="ecc-file-chips" style={{ marginBottom: 6 }}>
                        {cond.existingDocuments.map((doc, di) => (
                          <a
                            key={di}
                            className="ecc-file-chip"
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none" }}
                          >
                            <i className="fas fa-file" style={{ fontSize: 10, flexShrink: 0 }} />
                            <span>{doc.fileName}</span>
                            <i className="fas fa-external-link-alt" style={{ fontSize: 9, opacity: 0.6 }} />
                          </a>
                        ))}
                      </div>
                    )}
                    <label className="ecc-file-label" htmlFor={`doc_upload_${i}`}>
                      <i className="fas fa-paperclip" style={{ fontSize: 13 }} />
                      {cond.documents.length === 0 ? "Attach files…" : "Attach more files…"}
                    </label>
                    <input
                      id={`doc_upload_${i}`}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="ecc-file-input"
                      onChange={(e) => {
                        const ALLOWED_EXTS = /\.(pdf|doc|docx|jpg|jpeg|png)$/i;
                        const MAX_BYTES = 6 * 1024 * 1024;
                        const incoming = Array.from(e.target.files);
                        const valid = [];
                        incoming.forEach((file) => {
                          if (!ALLOWED_EXTS.test(file.name)) {
                            showToast("error", `"${file.name}" is not allowed. Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG.`);
                            return;
                          }
                          if (file.size > MAX_BYTES) {
                            showToast("error", `"${file.name}" exceeds the 6 MB size limit.`);
                            return;
                          }
                          valid.push(file);
                        });
                        if (valid.length > 0) upd(i, "documents", [...cond.documents, ...valid]);
                        e.target.value = "";
                      }}
                    />
                    {cond.documents.length > 0 && (
                      <div className="ecc-file-chips">
                        {cond.documents.map((f, fi) => (
                          <div className="ecc-file-chip" key={fi}>
                            <i className="fas fa-file" style={{ fontSize: 10, flexShrink: 0 }} />
                            <span>{f.name}</span>
                            <button
                              type="button"
                              className="ecc-file-chip-rm"
                              onClick={() => upd(i, "documents", cond.documents.filter((_, di) => di !== fi))}
                            >
                              <i className="fas fa-times" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </Field>
                </div>
              </div>

              {/* Completion Date */}
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--body-color-less2)" }}>
                <Field label="Completion Date" req error={errors[`cond_${i}_completionDate`]}>
                  <input
                    type="date"
                    className={`ecc-input${errors[`cond_${i}_completionDate`] ? " error" : ""}`}
                    value={cond.completionDate || ""}
                    onChange={(e) => upd(i, "completionDate", e.target.value)}
                    style={{ maxWidth: 320 }}
                  />
                </Field>
              </div>

              {/* Release Trigger */}
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--body-color-less2)" }}>
                <div className="ecc-label" style={{ marginBottom: 10 }}>
                  <i className="fas fa-unlock-alt" style={{ fontSize: 11, opacity: .7 }} /> Release Trigger
                </div>
                <div className="ecc-radio-group">
                  <label className="ecc-radio-opt">
                    <input
                      type="radio"
                      name={`milestoneReleaseType_${i}`}
                      value="on_approval"
                      checked={cond.milestoneReleaseType === "on_approval"}
                      onChange={() => upd(i, "milestoneReleaseType", "on_approval")}
                    />
                    On Approval
                  </label>
                  <label className="ecc-radio-opt">
                    <input
                      type="radio"
                      name={`milestoneReleaseType_${i}`}
                      value="release_date"
                      checked={cond.milestoneReleaseType === "release_date"}
                      onChange={() => upd(i, "milestoneReleaseType", "release_date")}
                    />
                    Release Date
                  </label>
                </div>
                {/* Approver picker when milestone trigger is On Approval */}
                {cond.milestoneReleaseType === "on_approval" && (
                  <div style={{ marginTop: 12 }}>
                    <div className="ecc-label" style={{ marginBottom: 8, fontSize: 11 }}>
                      <i className="fas fa-user-check" style={{ fontSize: 10, opacity: .7 }} /> Select Approvers for this milestone
                    </div>
                    {approvers.length === 0 ? (
                      <div className="ecc-approver-pick-empty">
                        No approvers added — go back to the Approvers step.
                      </div>
                    ) : (
                      <div className="ecc-approver-pick-list">
                        {approvers.map((a, ai) => (
                          <label className="ecc-approver-pick-opt" key={ai}>
                            <input
                              type="checkbox"
                              checked={(cond.milestoneApprovers || []).includes(ai)}
                              onChange={() => toggleMilestoneApprover(i, ai)}
                            />
                            <span>
                              <span className="ecc-approver-pick-name">{a.name || `Approver ${ai + 1}`}</span>
                              {a.role && <span className="ecc-approver-pick-role">{a.role}</span>}
                            </span>
                            {a.email && <span className="ecc-approver-pick-email">{a.email}</span>}
                          </label>
                        ))}
                      </div>
                    )}
                    {errors[`cond_${i}_milestoneApprovers`] && (
                      <p className="ecc-err">{errors[`cond_${i}_milestoneApprovers`]}</p>
                    )}
                  </div>
                )}

                {cond.milestoneReleaseType === "release_date" && (
                  <div style={{ marginTop: 12 }}>
                    <Field label="Release Date" req error={errors[`cond_${i}_milestoneReleaseDate`]}>
                      <input
                        type="date"
                        className={`ecc-input${errors[`cond_${i}_milestoneReleaseDate`] ? " error" : ""}`}
                        value={cond.milestoneReleaseDate || ""}
                        onChange={(e) => upd(i, "milestoneReleaseDate", e.target.value)}
                        style={{ maxWidth: 320 }}
                      />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
            <button
              className="ecc-btn-add"
              onClick={add}
              disabled={conditions.length >= 10}
              style={{ opacity: conditions.length >= 10 ? .45 : 1, cursor: conditions.length >= 10 ? "not-allowed" : "pointer" }}
            >
              <i className="fas fa-plus" style={{ fontSize: 11 }} /> Add Milestone
            </button>
            {conditions.length >= 10 && (
              <span className="ecc-hint">Maximum 10 milestones reached.</span>
            )}
          </div>

        </div>
      )}

      {/* ── Release Date view ── */}
      {releaseType === "date" && (
        <div className="ecc-section">
          <div className="ecc-section-hdr">
            <p className="ecc-section-title">
              <i className="fas fa-calendar-check" style={{ fontSize: 11 }} /> Release Date
            </p>
          </div>
          <Field label="Release Date" req error={errors.releaseDate}>
            <input
              type="date"
              className={`ecc-input${errors.releaseDate ? " error" : ""}`}
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
              style={{ maxWidth: 340 }}
            />
          </Field>
        </div>
      )}

      {/* Terms */}
      <div className="ecc-section">
        <div className="ecc-section-hdr">
          <p className="ecc-section-title">
            <i className="fas fa-file-alt" style={{ fontSize: 11 }} /> Terms &amp; Conditions
          </p>
        </div>
        <Field label="Contract Terms" opt hint={!errors.terms ? "General terms that govern this escrow agreement (min 10, max 5000 characters if provided)" : undefined} error={errors.terms}>
          <TermsEditor value={terms} onChange={setTerms} hasError={!!errors.terms} />
        </Field>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 4 — Payees
// ─────────────────────────────────────────────────────────────────
function StepPayees({ payees, setPayees, currency, totalAmount, errors }) {
  const add = () =>
    setPayees((p) => [...p, { name: "", email: "", wallet: "", percentage: "" }]);
  const rm = (i) => setPayees((p) => p.filter((_, idx) => idx !== i));
  const upd = (i, key, val) =>
    setPayees((p) => p.map((item, idx) => (idx === i ? { ...item, [key]: val } : item)));

  // Auto-distribute allocation equally whenever the number of payees changes.
  // For 1 payee → 100%. For n payees → floor(100/n) each, first (100 % n) payees
  // get one extra point so the total is always exactly 100%.
  useEffect(() => {
    const n = payees.length;
    if (n === 0) return;
    const base = Math.floor(100 / n);
    const extra = 100 - base * n;
    setPayees((prev) =>
      prev.map((p, i) => ({ ...p, percentage: String(i < extra ? base + 1 : base) }))
    );
  }, [payees.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const allocatedPct = payees.reduce((sum, p) => sum + (parseFloat(p.percentage) || 0), 0);

  return (
    <div className="ecc-pane-in">
      <div className="ecc-section">
        <div className="ecc-section-hdr">
          <p className="ecc-section-title">
            <i className="fas fa-handshake" style={{ fontSize: 11 }} /> Payee Details
          </p>
        </div>

        {payees.map((payee, i) => (
          <div className="ecc-participant" key={i}>
            <div className="ecc-participant-num">Payee {i + 1}</div>
            {payees.length > 1 && (
              <button className="ecc-participant-rm" onClick={() => rm(i)}>
                <i className="fas fa-times" />
              </button>
            )}
            <div className="ecc-grid-2">
              <Field label="Full Name" req error={errors[`payee_${i}_name`]}>
                <input
                  className={`ecc-input${errors[`payee_${i}_name`] ? " error" : ""}`}
                  placeholder="e.g. Alice Johnson"
                  value={payee.name}
                  onChange={(e) => upd(i, "name", e.target.value)}
                />
              </Field>
              <Field label="Email Address" req error={errors[`payee_${i}_email`]}>
                <input
                  className={`ecc-input${errors[`payee_${i}_email`] ? " error" : ""}`}
                  placeholder="alice@example.com"
                  value={payee.email}
                  onChange={(e) => upd(i, "email", e.target.value)}
                />
              </Field>
              <Field label="Wallet Address" opt error={errors[`payee_${i}_wallet`]}>
                <input
                  className={`ecc-input${errors[`payee_${i}_wallet`] ? " error" : ""}`}
                  placeholder="0x..."
                  value={payee.wallet}
                  onChange={(e) => upd(i, "wallet", e.target.value)}
                />
              </Field>
              <Field label="Allocated Percentage" req error={errors[`payee_${i}_amount`]}>
                <div className="ecc-pct-row">
                  <input
                    className={`ecc-input${errors[`payee_${i}_amount`] ? " error" : ""}`}
                    type="number"
                    min="1"
                    max="100"
                    placeholder="e.g. 50"
                    value={payee.percentage}
                    onChange={(e) => upd(i, "percentage", e.target.value)}
                  />
                  <span className="ecc-pct-suffix">%</span>
                </div>
              </Field>
            </div>
          </div>
        ))}

        <button className="ecc-btn-add" onClick={add} style={{ marginTop: 4 }}>
          <i className="fas fa-plus" style={{ fontSize: 11 }} /> Add Another Payee
        </button>

        <div className="ecc-total-bar" style={{ marginTop: 18 }}>
          <span className="ecc-total-label">
            Total Allocated
            {allocatedPct > 100 && <span style={{ color: "#f87171", marginLeft: 8 }}>⚠ Over-allocated by {allocatedPct - 100}%</span>}
          </span>
          <span className="ecc-total-value" style={{ color: allocatedPct > 100 ? "#f87171" : allocatedPct === 100 ? "#10b981" : "var(--btn-color)" }}>
            {allocatedPct}% / 100%
          </span>
        </div>
        {errors.payeeTotalPct && (
          <span className="ecc-err" style={{ marginTop: 6, display: "block" }}>{errors.payeeTotalPct}</span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 5 — Review
// ─────────────────────────────────────────────────────────────────
function StepReview({ contractName, contractDescription, approvers, approvalType, releaseApprovers, conditions, payees, currency, terms, releaseType, releaseDate }) {
  const releaseSummaryLabel = {
    on_approval: "On Approval",
    date: "Release Date",
    milestone: `${conditions.length} Milestone${conditions.length !== 1 ? "s" : ""}`,
  }[releaseType] || "";

  return (
    <div className="ecc-pane-in">

      {/* Contract details summary */}
      <div className="ecc-review-section">
        <p className="ecc-review-title">Contract Details</p>
        <div className="ecc-review-card" style={{ gridColumn: "1 / -1" }}>
          <div className="ecc-review-card-label">Contract Name</div>
          <div className="ecc-review-card-value">{contractName || "—"}</div>
          {contractDescription && (
            <div className="ecc-review-card-sub" style={{ marginTop: 6, lineHeight: 1.55 }}>{contractDescription}</div>
          )}
        </div>
      </div>

      {/* Release summary pill */}
      <div className="ecc-section" style={{ textAlign: "center", padding: "20px 22px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--text-color)", opacity: .5, marginBottom: 8 }}>
          Release Type
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--btn-color)" }}>{releaseSummaryLabel}</div>
        <div style={{ fontSize: 12, color: "var(--text-color)", opacity: .5, marginTop: 6 }}>
          {payees.length} payee{payees.length !== 1 ? "s" : ""} · Total escrow amount set in Fund step
        </div>
      </div>

      {/* Approvers */}
      <div className="ecc-review-section">
        <p className="ecc-review-title">
          Approvers
          {approvers.length > 0 && (
            <span className="ecc-review-pill" style={{ marginLeft: 8 }}>
              {approvalType === "all"
                ? `All ${approvers.length} must approve`
                : `${approvalType} of ${approvers.length} must approve`}
            </span>
          )}
        </p>
        {approvers.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .45, margin: 0 }}>No approvers — payer confirms delivery directly.</p>
        ) : (
          <div className="ecc-review-grid">
            {approvers.map((a, i) => (
              <div className="ecc-review-card" key={i}>
                <div className="ecc-review-card-label">{a.role || "Approver"} {i + 1}</div>
                <div className="ecc-review-card-value">{a.name || "—"}</div>
                <div className="ecc-review-card-sub">{a.email || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Release Conditions */}
      <div className="ecc-review-section">
        <p className="ecc-review-title">Release</p>

        {releaseType === "on_approval" && (
          <>
            <div className="ecc-review-grid">
              <div className="ecc-review-card">
                <div className="ecc-review-card-label">Release Trigger</div>
                <div className="ecc-review-card-value">On Approval</div>
                <div className="ecc-review-card-sub">Releases when required approvers confirm</div>
              </div>
            </div>
            {approvers.length > 0 && (
              <div className="ecc-review-card" style={{ marginTop: 10 }}>
                <div className="ecc-review-card-label" style={{ marginBottom: 8 }}>Assigned Approvers</div>
                {releaseApprovers.length === 0 ? (
                  <div className="ecc-review-card-sub">All {approvers.length} approver{approvers.length !== 1 ? "s" : ""} assigned</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {releaseApprovers.map((ai) => approvers[ai] && (
                      <div key={ai} style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ fontWeight: 600 }}>{approvers[ai].name || `Approver ${ai + 1}`}</span>
                        {approvers[ai].role && <span style={{ fontSize: 11, opacity: .55, padding: "1px 6px", borderRadius: 4, background: "rgba(100,116,139,.1)" }}>{approvers[ai].role}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {releaseType === "date" && (
          <div className="ecc-review-grid">
            <div className="ecc-review-card">
              <div className="ecc-review-card-label">Release Date</div>
              <div className="ecc-review-card-value">
                {releaseDate ? new Date(releaseDate).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}
              </div>
            </div>
          </div>
        )}

        {releaseType === "milestone" && (
          <ul className="ecc-review-list">
            {conditions.map((c, i) => (
              <li className="ecc-review-list-item" key={i}>
                <span className="ecc-review-list-item-num">{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{c.label || "Untitled"}</span>
                    <span style={{ fontSize: 12, opacity: .6 }}>{c.percentage || 0}%</span>
                    {c.completionDate && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: "rgba(99,102,241,.12)", color: "#818cf8",
                      }}>
                        <i className="fas fa-calendar-alt" style={{ fontSize: 10 }} />
                        {new Date(c.completionDate).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    )}
                    {c.milestoneReleaseType === "on_approval" ? (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: "rgba(16,185,129,.10)", color: "#10b981",
                      }}>
                        <i className="fas fa-check-circle" style={{ fontSize: 10 }} />
                        On Approval
                      </span>
                    ) : c.milestoneReleaseDate ? (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: "rgba(245,158,11,.10)", color: "#f59e0b",
                      }}>
                        <i className="fas fa-clock" style={{ fontSize: 10 }} />
                        {new Date(c.milestoneReleaseDate).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    ) : null}
                  </div>
                  {c.deliverables && (
                    <div style={{ fontSize: 12, opacity: .6, marginTop: 3 }}>{c.deliverables}</div>
                  )}
                  {((c.documents?.length || 0) + (c.existingDocuments?.length || 0)) > 0 && (
                    <div style={{ fontSize: 11, opacity: .55, marginTop: 4 }}>
                      <i className="fas fa-paperclip" style={{ marginRight: 4 }} />
                      {(c.documents?.length || 0) + (c.existingDocuments?.length || 0)} document{((c.documents?.length || 0) + (c.existingDocuments?.length || 0)) !== 1 ? "s" : ""} attached
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Payees */}
      <div className="ecc-review-section">
        <p className="ecc-review-title">Payees</p>
        <div className="ecc-review-grid">
          {payees.map((p, i) => (
            <div className="ecc-review-card" key={i}>
              <div className="ecc-review-card-label">Payee {i + 1}</div>
              <div className="ecc-review-card-value">{p.name || "—"}</div>
              <div className="ecc-review-card-sub">{p.email || "—"}</div>
              {p.percentage && (
                <div className="ecc-review-card-sub" style={{ fontWeight: 700, color: "var(--btn-color)", marginTop: 4 }}>
                  {parseFloat(p.percentage) || 0}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Terms */}
      {terms && terms.replace(/<[^>]*>/g, "").trim() && (
        <div className="ecc-review-section">
          <p className="ecc-review-title">Terms &amp; Conditions</p>
          <div className="ecc-terms-review" dangerouslySetInnerHTML={{ __html: terms }} />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Step 5 — Fund
// ─────────────────────────────────────────────────────────────────
const FUND_METHODS = [
  {
    key: "paypal",
    title: "PayPal",
    desc: "Fast and secure payment via your PayPal account or linked card.",
    icon: "fab fa-paypal",
    color: "#0070e0",
  },
  {
    key: "address",
    title: "By receiving address",
    desc: "Send funds directly to the escrow receiving wallet address.",
    icon: "fas fa-wallet",
    color: "#2563eb",
  },
  {
    key: "bank",
    title: "Bank Transfer",
    desc: "Wire funds directly from your bank account. Processing may take 1–3 days.",
    icon: "fas fa-university",
    color: "#e03535",
  },
];

function StepFund({
  payers, setPayers,
  youContribution, setYouContribution,
  fundTotalAmount, setFundTotalAmount,
  fundMethod, setFundMethod,
  errors, currency, onPayPalApprove,
}) {
  const add = () => setPayers((p) => [...p, { name: "", email: "", wallet: "", contribution: "" }]);
  const rm  = (i) => setPayers((p) => p.filter((_, idx) => idx !== i));
  const upd = (i, key, val) => setPayers((p) => p.map((item, idx) => (idx === i ? { ...item, [key]: val } : item)));

  const totalAmt     = parseFloat(fundTotalAmount) || 0;
  const coPayerTotal = payers.reduce((sum, p) => sum + (parseFloat(p.contribution) || 0), 0);
  const youPct       = parseFloat(youContribution) || 0;
  const youAmt       = totalAmt > 0 ? (totalAmt * youPct / 100) : 0;

  const fmtPct = (n) => (n % 1 === 0 ? n : n.toFixed(1));
  const fmtAmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Crypto-address state ──────────────────────────────────────
  const [selectedCrypto, setSelectedCrypto] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [cryptoConvertedAmt, setCryptoConvertedAmt] = useState(null);
  const [cryptoPriceLoading, setCryptoPriceLoading] = useState(false);

  const needsNetwork = ["USDT", "USDC"].includes(selectedCrypto);

  useEffect(() => {
    if (fundMethod !== "address") {
      setSelectedCrypto("");
      setSelectedNetwork("");
      setCryptoConvertedAmt(null);
    }
  }, [fundMethod]);

  useEffect(() => { setSelectedNetwork(""); }, [selectedCrypto]);

  useEffect(() => {
    if (!selectedCrypto) { setCryptoConvertedAmt(null); return; }
    setCryptoPriceLoading(true);
    fetch(`${WEBSERVICE}/merchant/getMarketPriceWithBaseCurrency?currency=${selectedCrypto}&baseCurrency=USD`)
      .then((r) => r.json())
      .then((res) => {
        if (res.error !== "0" && res.error !== 0) return;
        const price = parseFloat(res.price);
        if (price > 0 && totalAmt > 0) setCryptoConvertedAmt((totalAmt / price).toFixed(8));
      })
      .catch(() => {})
      .finally(() => setCryptoPriceLoading(false));
  }, [selectedCrypto, totalAmt]);

  return (
    <div className="ecc-pane-in">

      {/* ── Section 1: Total Escrow Amount ── */}
      <div className="ecc-section">
        <div className="ecc-section-hdr">
          <p className="ecc-section-title">
            <i className="fas fa-dollar-sign" style={{ fontSize: 11 }} /> Total Escrow Amount
          </p>
        </div>
        <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .6, margin: "0 0 18px", lineHeight: 1.55 }}>
          Enter the full amount that will be held in escrow for this contract.
        </p>
        <Field label="Total Escrow Amount (USD)" req error={errors.fundTotalAmount}>
          <div className="ecc-fund-amount-input-wrap" style={{ maxWidth: 300 }}>
            <span className="ecc-fund-amount-prefix">$</span>
            <input
              className={`ecc-fund-amount-field${errors.fundTotalAmount ? " error" : ""}`}
              type="number"
              min="0"
              placeholder="0.00"
              value={fundTotalAmount}
              onChange={(e) => setFundTotalAmount(e.target.value)}
            />
            <span className="ecc-fund-amount-currency">USD</span>
          </div>
        </Field>
      </div>

      {/* ── Section 2: Payers ── */}
      {totalAmt > 0 && (
        <div className="ecc-section">
          <div className="ecc-section-hdr">
            <p className="ecc-section-title">
              <i className="fas fa-users" style={{ fontSize: 11 }} /> Payers
              <span style={{ marginLeft: 8, fontWeight: 500, fontSize: 11, opacity: .55 }}>Optional</span>
            </p>
          </div>
          <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .6, margin: "0 0 18px", lineHeight: 1.55 }}>
            Add all payers and assign each one a percentage of the total amount.
          </p>

          {/* You (Payer) — fixed card */}
          <div className="ecc-participant" style={{ background: "color-mix(in srgb, var(--btn-color) 5%, var(--body-color))", borderColor: "color-mix(in srgb, var(--btn-color) 25%, transparent)" }}>
            <div className="ecc-participant-num" style={{ color: "var(--btn-color)", opacity: 1 }}>
              <i className="fas fa-user" style={{ fontSize: 10, marginRight: 5 }} />You (Payer)
            </div>
            <div style={{ maxWidth: 200 }}>
              <Field label="Your Contribution %" req>
                <div className="ecc-pct-row">
                  <input
                    className="ecc-input"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="e.g. 100"
                    value={youContribution}
                    onChange={(e) => setYouContribution(e.target.value)}
                  />
                  <span className="ecc-pct-suffix">%</span>
                </div>
              </Field>
            </div>
            {youPct > 0 && (
              <div style={{ marginTop: 10, padding: "8px 12px", background: "color-mix(in srgb, var(--btn-color) 6%, transparent)", borderRadius: 8, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ opacity: .65 }}>You will pay:</span>
                <span style={{ fontWeight: 700, color: "var(--btn-color)" }}>${fmtAmt(youAmt)} USD</span>
              </div>
            )}
          </div>

          {/* Co-payer cards */}
          {payers.map((payer, i) => {
            const payerAmt = totalAmt * (parseFloat(payer.contribution) || 0) / 100;
            return (
              <div className="ecc-participant" key={i}>
                <div className="ecc-participant-num">Payer {i + 1}</div>
                <button className="ecc-participant-rm" onClick={() => rm(i)}>
                  <i className="fas fa-times" />
                </button>
                <div className="ecc-grid-3">
                  <Field label="Full Name" req error={errors[`payer_${i}_name`]}>
                    <input
                      className={`ecc-input${errors[`payer_${i}_name`] ? " error" : ""}`}
                      placeholder="e.g. John Smith"
                      value={payer.name}
                      onChange={(e) => upd(i, "name", e.target.value)}
                    />
                  </Field>
                  <Field label="Email Address" req error={errors[`payer_${i}_email`]}>
                    <input
                      className={`ecc-input${errors[`payer_${i}_email`] ? " error" : ""}`}
                      placeholder="john@example.com"
                      value={payer.email}
                      onChange={(e) => upd(i, "email", e.target.value)}
                    />
                  </Field>
                  <Field label="Contribution %" req error={errors[`payer_${i}_contribution`]}>
                    <div className="ecc-pct-row">
                      <input
                        className={`ecc-input${errors[`payer_${i}_contribution`] ? " error" : ""}`}
                        type="number"
                        min="1"
                        max="99"
                        placeholder="e.g. 50"
                        value={payer.contribution}
                        onChange={(e) => upd(i, "contribution", e.target.value)}
                      />
                      <span className="ecc-pct-suffix">%</span>
                    </div>
                  </Field>
                </div>
                {(parseFloat(payer.contribution) || 0) > 0 && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "color-mix(in srgb, var(--btn-color) 6%, transparent)", borderRadius: 8, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ opacity: .65 }}>They will pay:</span>
                    <span style={{ fontWeight: 700, color: "var(--btn-color)" }}>${fmtAmt(payerAmt)} USD</span>
                  </div>
                )}
              </div>
            );
          })}

          <button className="ecc-btn-add" onClick={add} style={{ marginTop: 4 }}>
            <i className="fas fa-plus" style={{ fontSize: 11 }} /> Add Another Payer
          </button>

        </div>
      )}

      {/* ── Section 3: Payment Breakdown ── */}
      {totalAmt > 0 && (
        <div className="ecc-section">
          <div className="ecc-section-hdr">
            <p className="ecc-section-title">
              <i className="fas fa-chart-pie" style={{ fontSize: 11 }} /> Payment Breakdown
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* You (Payer) row */}
            {youPct > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "color-mix(in srgb, var(--btn-color) 5%, var(--body-color))", borderRadius: 8, border: "1px solid color-mix(in srgb, var(--btn-color) 25%, transparent)" }}>
                <div style={{ flex: 1, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>You (Payer)</span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--btn-color)", minWidth: 48, textAlign: "right" }}>{youPct}%</span>
                <span style={{ fontSize: 13, opacity: .4, margin: "0 4px" }}>→</span>
                <span style={{ fontSize: 14, fontWeight: 700, minWidth: 90, textAlign: "right" }}>
                  ${fmtAmt(youAmt)}
                </span>
              </div>
            )}

            {/* Co-payer rows */}
            {payers.map((p, i) => {
              const pct = parseFloat(p.contribution) || 0;
              const amt = totalAmt * pct / 100;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--body-color)", borderRadius: 8, border: "1px solid var(--body-color-less2)" }}>
                  <div style={{ flex: 1, fontSize: 13 }}>
                    <span style={{ fontWeight: 600 }}>{p.name || `Payer ${i + 1}`}</span>
                    {p.email && <span style={{ fontSize: 11, opacity: .5, marginLeft: 8 }}>{p.email}</span>}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--btn-color)", minWidth: 48, textAlign: "right" }}>{pct}%</span>
                  <span style={{ fontSize: 13, opacity: .4, margin: "0 4px" }}>→</span>
                  <span style={{ fontSize: 14, fontWeight: 700, minWidth: 90, textAlign: "right" }}>
                    ${fmtAmt(amt)}
                  </span>
                </div>
              );
            })}

            {/* Total row */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--body-color-less)", borderRadius: 8, borderTop: "2px solid var(--body-color-less2)", marginTop: 2 }}>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>Total</div>
              <span style={{ fontSize: 13, fontWeight: 700, minWidth: 48, textAlign: "right", color: (youPct + coPayerTotal) === 100 ? "#10b981" : "var(--btn-color)" }}>
                {fmtPct(youPct + coPayerTotal)}%
              </span>
              <span style={{ fontSize: 13, opacity: .4, margin: "0 4px" }}>→</span>
              <span style={{ fontSize: 16, fontWeight: 700, minWidth: 90, textAlign: "right", color: "var(--btn-color)" }}>
                ${fmtAmt(totalAmt)}
              </span>
            </div>
          </div>

          {/* Email notice for payers */}
          {payers.length > 0 && (
            <div style={{ marginTop: 14, padding: "11px 14px", background: "color-mix(in srgb, #10b981 8%, transparent)", border: "1px solid color-mix(in srgb, #10b981 25%, transparent)", borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <i className="fas fa-envelope" style={{ fontSize: 14, color: "#10b981", marginTop: 1, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#10b981", marginBottom: 3 }}>Payers Will Be Notified by Email</div>
                <div style={{ fontSize: 12, color: "var(--text-color)", opacity: .7, lineHeight: 1.5 }}>
                  {payers.length === 1 ? "1 payer" : `${payers.length} payers`} will receive an email invitation to pay their share of the escrow.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Section 4: Your Payment ── */}
      {totalAmt > 0 && (
        <div className="ecc-section">
          <div className="ecc-section-hdr">
            <p className="ecc-section-title">
              <i className="fas fa-credit-card" style={{ fontSize: 11 }} /> Your Payment
            </p>
          </div>
          <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .6, margin: "0 0 20px", lineHeight: 1.55 }}>
            You need to pay{" "}
            <strong style={{ color: "var(--btn-color)" }}>${fmtAmt(youPct > 0 ? youAmt : totalAmt)} USD</strong>.{" "}
            Select your preferred payment method below.
          </p>

          <div className="ecc-fund-grid">
            {FUND_METHODS.map((m) => (
              <label
                key={m.key}
                className={`ecc-fund-card${fundMethod === m.key ? " selected" : ""}`}
                style={{ "--ecc-fund-color": m.color }}
                onClick={() => setFundMethod(m.key)}
              >
                <input type="radio" name="fundMethod" value={m.key} checked={fundMethod === m.key} onChange={() => setFundMethod(m.key)} />
                <div className="ecc-fund-card-check">
                  {fundMethod === m.key && <i className="fas fa-check" />}
                </div>
                <div className="ecc-fund-card-icon">
                  <i className={m.icon} />
                </div>
                <div className="ecc-fund-card-title">{m.title}</div>
                <div className="ecc-fund-card-desc">{m.desc}</div>
              </label>
            ))}
          </div>

          {errors.fundMethod && (
            <span className="ecc-err" style={{ marginTop: 12, display: "block" }}>{errors.fundMethod}</span>
          )}

          {/* Amount + method-specific panel */}
          {fundMethod && (
            <div className="ecc-fund-amount-box">
              <div className="ecc-fund-amount-label">Your Payment Amount (USD)</div>
              <div className="ecc-fund-amount-input-wrap">
                <span className="ecc-fund-amount-prefix">$</span>
                <input
                  className="ecc-fund-amount-field"
                  type="number"
                  readOnly
                  value={(youPct > 0 ? youAmt : totalAmt).toFixed(2)}
                />
                <span className="ecc-fund-amount-currency">USD</span>
              </div>

              {/* PayPal button */}
              {fundMethod === "paypal" && totalAmt > 0 && (
                <div className="ecc-paypal-panel">
                  <div className="ecc-paypal-divider">
                    <div className="ecc-paypal-divider-line" />
                    <span className="ecc-paypal-divider-text">Pay securely with PayPal</span>
                    <div className="ecc-paypal-divider-line" />
                  </div>
                  <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay", height: 45 }}
                      createOrder={(_data, actions) =>
                        actions.order.create({
                          purchase_units: [{ amount: { value: totalAmt.toFixed(2), currency_code: "USD" } }],
                        })
                      }
                      onApprove={(_data, actions) =>
                        actions.order.capture().then((details) => {
                          if (onPayPalApprove) onPayPalApprove(details);
                        })
                      }
                    />
                  </PayPalScriptProvider>
                </div>
              )}

              {/* Bank Transfer */}
              {fundMethod === "bank" && (
                <div className="ecc-bank-panel">
                  <div className="ecc-paypal-divider">
                    <div className="ecc-paypal-divider-line" />
                    <span className="ecc-paypal-divider-text">Transfer instructions</span>
                    <div className="ecc-paypal-divider-line" />
                  </div>

                  <div className="ecc-bank-notice">
                    <p className="ecc-bank-notice-title">
                      <i className="fas fa-info-circle" style={{ fontSize: 12 }} />
                      This is the amount in USD you must transfer.
                    </p>
                    <ul className="ecc-bank-notice-list">
                      <li>The amount displayed here must reach us in full to avoid delays in processing.</li>
                      <li>Bank charges or platform rate adjustment must be made at your end.</li>
                      <li>Your Settlement Wallet balance will be updated as soon as the transfer reaches us.</li>
                    </ul>
                  </div>

                  <p className="ecc-bank-details-title">
                    <i className="fas fa-university" style={{ fontSize: 11 }} />
                    Make the transfer to the bank given below.
                  </p>

                  <table className="ecc-bank-table">
                    <tbody>
                      {[
                        ["Beneficiary Name",    "DigitalEdge Advisors Inc."],
                        ["Beneficiary Address", "8 The Green, Dover, DE 19901"],
                        ["Bank Name",           "Choice Financial Group"],
                        ["Bank Address",        "4501 23rd Avenue S Fargo, ND 58104, USA"],
                        ["Account No",          "202444191696"],
                        ["Account Type",        "Checking"],
                        ["Routing No",          "091311229"],
                        ["Swift Code",          "CHFGUS44021"],
                      ].map(([label, value]) => (
                        <tr key={label}>
                          <td>{label}</td>
                          <td>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* By receiving address — crypto selector + equivalent price */}
              {fundMethod === "address" && (
                <div className="ecc-crypto-panel">
                  <div className="ecc-paypal-divider">
                    <div className="ecc-paypal-divider-line" />
                    <span className="ecc-paypal-divider-text">Select cryptocurrency</span>
                    <div className="ecc-paypal-divider-line" />
                  </div>

                  <div className="ecc-crypto-list">
                    {CRYPTO_MASTER_LIST.map((coin) => {
                      const isSelected = selectedCrypto === coin.currency_code;
                      return (
                        <div
                          key={coin.currency_code}
                          className={`ecc-crypto-row${isSelected ? " selected" : ""}`}
                          onClick={() => setSelectedCrypto(isSelected ? "" : coin.currency_code)}
                        >
                          <img
                            src={coin.logo}
                            alt={coin.currency_code}
                            className="ecc-crypto-logo"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                          <span className="ecc-crypto-code">{coin.currency_code}</span>
                          <span className="ecc-crypto-name">{coin.currency_name}</span>
                          <div className="ecc-crypto-check">
                            {isSelected && <i className="fas fa-check" style={{ fontSize: 7, color: "#fff" }} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {needsNetwork && (
                    <div className="ecc-network-section">
                      <div className="ecc-network-label">Select Network</div>
                      <div className="ecc-network-options">
                        {[
                          { key: "ERC", name: "ERC-20", desc: "Ethereum network" },
                          { key: "TRC", name: "TRC-20", desc: "Tron network" },
                        ].map((net) => (
                          <label key={net.key} className="ecc-network-opt">
                            <input
                              type="radio"
                              name="cryptoNetwork"
                              value={net.key}
                              checked={selectedNetwork === net.key}
                              onChange={() => setSelectedNetwork(net.key)}
                            />
                            <div>
                              <div className="ecc-network-opt-name">{net.name}</div>
                              <div className="ecc-network-opt-desc">{net.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedCrypto && (!needsNetwork || selectedNetwork) && (
                    <div className="ecc-crypto-equiv">
                      <span className="ecc-crypto-equiv-label">
                        Equivalent in {selectedCrypto}{selectedNetwork ? ` (${selectedNetwork === "ERC" ? "ERC-20" : "TRC-20"})` : ""}
                      </span>
                      {cryptoPriceLoading ? (
                        <span className="ecc-crypto-spinner" />
                      ) : cryptoConvertedAmt ? (
                        <span className="ecc-crypto-equiv-value">
                          {cryptoConvertedAmt} {selectedCrypto}
                        </span>
                      ) : (
                        <span style={{ fontSize: 13, color: "var(--text-color)", opacity: .4 }}>—</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
export default function EscrowContractCreate() {
  const history = useHistory();
  const { escrowId } = useParams();

  const [escrowAction, setEscrowAction] = useState(escrowId ? "UPDATE" : "INSERT");

  const [step, setStep] = useState(0);
  const [stepCount, setStepCount] = useState(1);
  const [errors, setErrors] = useState({});
  const [contractNo, setContractNo] = useState(escrowId || null);
  const [apiLoading, setApiLoading] = useState(false);

  // Currencies from API
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);

  useEffect(() => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    FetchUsdBtcLedgerAmount({ merchant_id: merchantId })
      .then((res) => {
        const list = (res.data?.[0]?.coin_balance || []).filter((c) => c.currency_type === "2");
        setCurrencies(list);
        if (list.length > 0) setCurrency(list[0].currency_code);
      })
      .catch(() => {
        // keep currency as empty string on failure; user can retry by refreshing
      })
      .finally(() => setCurrenciesLoading(false));
  }, []);

  // Form state
  const [contractName, setContractName] = useState("");
  const [contractDescription, setContractDescription] = useState("");

  const [approvers, setApprovers] = useState([{ role: "Inspector", name: "", email: "" }]);
  const [approvalType, setApprovalType] = useState("all");
  const [approvalConfigs, setApprovalConfigs] = useState([
    { approvalMode: "THRESHOLD", approvalThreshold: 1 },
  ]);

  useEffect(() => {
    if (approvers.length === 0) {
      setApprovalConfigs([{ approvalMode: null, approvalThreshold: null }]);
    } else {
      const threshold =
        approvalType === "all"
          ? approvers.length
          : parseInt(approvalType, 10) || approvers.length;
      setApprovalConfigs([{ approvalMode: "THRESHOLD", approvalThreshold: threshold }]);
    }
  }, [approvers.length, approvalType]);
  const [releaseType, setReleaseType] = useState("on_approval");
  const [releaseAmountType, setReleaseAmountType] = useState("amount");
  const [releaseDate, setReleaseDate] = useState("");
  const [releaseTotalAmount, setReleaseTotalAmount] = useState("");
  const [releaseTotalPercentage, setReleaseTotalPercentage] = useState("");
  const [conditions, setConditions] = useState([{ label: "", deliverables: "", documents: [], existingDocuments: [], completionDate: "", percentage: "", amountType: "percentage", milestoneReleaseType: "on_approval", milestoneReleaseDate: "", milestoneApprovers: [] }]);
  const [releaseApprovers, setReleaseApprovers] = useState([]);
  const [releaseConfigs, setReleaseConfigs] = useState([
    { releaseType: "ON_APPROVAL", releaseAmountType: "AMOUNT", totalReleaseAmount: 0, releaseDate: null, selectedApproverIndexes: [], autoReleaseEnabled: true },
  ]);

  useEffect(() => {
    const RELEASE_TYPE_MAP = { on_approval: "ON_APPROVAL", date: "RELEASE_DATE", milestone: "MILESTONE_BASED" };
    const AMOUNT_TYPE_MAP  = { amount: "AMOUNT", percentage: "PERCENTAGE" };
    const toIso = (d) => {
      if (!d) return null;
      if (d.includes("T")) return d.length === 16 ? `${d}:00` : d;
      return `${d}T00:00:00`;
    };

    const mappedReleaseType = RELEASE_TYPE_MAP[releaseType] || "ON_APPROVAL";
    const totalAmt = parseFloat(releaseTotalAmount) || 0;

    let config = {
      releaseType: mappedReleaseType,
      releaseAmountType: "PERCENTAGE",
      totalReleaseAmount: totalAmt,
      releaseDate: null,
      autoReleaseEnabled: true,
    };

    if (releaseType === "on_approval") {
      config.selectedApproverIndexes = releaseApprovers;
    } else if (releaseType === "date") {
      config.selectedApproverIndexes = [];
      config.releaseDate = toIso(releaseDate);
    } else if (releaseType === "milestone") {
      config.milestones = conditions.map((cond, idx) => {
        const pct = parseFloat(cond.percentage) || 0;
        const isByAmount = cond.amountType === "amount";
        const amt = isByAmount ? (totalAmt * pct / 100) : null;
        return {
          sequenceNo: idx + 1,
          title: cond.label || "",
          description: cond.deliverables || "",
          deliverables: cond.deliverables || "",
          completionDate: toIso(cond.completionDate),
          amountType: AMOUNT_TYPE_MAP[cond.amountType] || "PERCENTAGE",
          amount: isByAmount ? amt : null,
          percentage: !isByAmount ? pct : null,
          releaseTriggerType: cond.milestoneReleaseType === "date" ? "DATE" : "APPROVAL",
          releaseDate: toIso(cond.milestoneReleaseDate),
          assignedApproverIndexes: cond.milestoneApprovers || [],
          documents: cond.documents || [],
        };
      });
    }

    setReleaseConfigs([config]);
  }, [releaseType, releaseAmountType, releaseTotalAmount, releaseDate, releaseApprovers, conditions]);
  const [currency, setCurrency] = useState("");
  const [terms, setTerms] = useState("");
  const [payees, setPayees] = useState([{ name: "", email: "", wallet: "", percentage: "" }]);

  // ── Load existing contract when editing (escrowId in URL) ──────
  useEffect(() => {
    if (!escrowId) return;
    const RELEASE_TYPE_MAP     = { ON_APPROVAL: "on_approval", RELEASE_DATE: "date", MILESTONE_BASED: "milestone" };
    const AMOUNT_TYPE_MAP      = { AMOUNT: "amount", PERCENTAGE: "percentage" };
    const MILESTONE_TRIGGER_MAP = { APPROVAL: "on_approval", DATE: "date" };
    const merchantId = parseInt(localStorage.getItem(SITENAMEALIAS + "_merchant_id"), 10);
    fetchEscrowContractDetails(escrowId, merchantId)
      .then((data) => {
        if (data.status === false) {
          showToast("error", data.message || "Failed to load contract details.");
          return;
        }
        const contract = Array.isArray(data.data) && data.data[0];
        if (!contract) return;

        // Basic fields
        if (contract.contractTitle)       setContractName(contract.contractTitle);
        if (contract.contractDescription) setContractDescription(contract.contractDescription);
        if (contract.termsAndConditions)  setTerms(contract.termsAndConditions);
        if (contract.releaseType)         setReleaseType(RELEASE_TYPE_MAP[contract.releaseType] || "on_approval");
        if (contract.releaseAmountType)   setReleaseAmountType(AMOUNT_TYPE_MAP[contract.releaseAmountType] || "amount");
        if (contract.totalContractAmount != null) setReleaseTotalAmount(String(contract.totalContractAmount));

        // Participants — payees
        const payeeList = (contract.participants || []).filter((p) => p.role === "PAYEE");
        if (payeeList.length > 0) {
          setPayees(payeeList.map((p) => ({
            name:   p.name   || "",
            email:  p.email  || "",
            wallet: p.walletAddress || "",
            percentage: p.sharePercent != null ? String(p.sharePercent) : "",
          })));
        }

        // Participants — approvers
        const approverList = (contract.participants || []).filter((p) => p.role === "APPROVER");
        if (approverList.length > 0) {
          setApprovers(approverList.map((p) => ({
            role:  p.approverRole || "Inspector",
            name:  p.name  || "",
            email: p.email || "",
          })));
        }

        // Approval config
        if (contract.approvalModel) {
          setApprovalConfigs([{
            approvalMode:      contract.approvalModel,
            approvalThreshold: contract.approvalThreshold || 1,
          }]);
        }

        // Milestones → conditions
        if (Array.isArray(contract.milestones) && contract.milestones.length > 0) {
          const escrowDocs = Array.isArray(contract.escrowDocuments) ? contract.escrowDocuments : [];
          setConditions(contract.milestones.map((m) => ({
            label:               m.title || "",
            deliverables:        m.deliverables || m.description || "",
            documents:           [],
            existingDocuments:   escrowDocs.filter((d) => d.milestoneId === m.milestoneId),
            completionDate:      m.expectedCompletionDate
                                   ? m.expectedCompletionDate.substring(0, 16)
                                   : "",
            percentage:          m.releasePercentage != null ? String(m.releasePercentage) : "",
            amountType:          AMOUNT_TYPE_MAP[m.amountType] || "percentage",
            milestoneReleaseType: MILESTONE_TRIGGER_MAP[m.releaseTriggerType] || "on_approval",
            milestoneReleaseDate: m.releaseDate ? m.releaseDate.substring(0, 16) : "",
            milestoneApprovers:  Array.isArray(m.assignedApproverIndexes) ? m.assignedApproverIndexes : [],
          })));
        }

        // Auto-select the step where the draft was last saved (API is 1-indexed)
        if (contract.step != null) {
          const targetStep = contract.step - 1; // convert to 0-indexed
          setStep(targetStep >= 0 ? targetStep : 0);
          setStepCount(targetStep >= 0 ? targetStep + 1 : 1);
        }
      })
      .catch(() => {
        showToast("error", "Failed to load contract details.");
      });
  }, [escrowId]);

  const totalAmount = releaseType === "milestone"
    ? calcTotal(conditions)
    : (parseFloat(releaseTotalAmount) || 0);

  // ── Validation per step ─────────────────────────────────────
  // Steps: 0=details, 1=approvers, 2=conditions, 3=payees, 4=review
  const validateStep = (idx) => {
    const e = {};
    if (idx === 0) {
      const name = contractName.trim();
      if (!name) {
        e.contractName = "Contract name is required";
      } else if (name.length < 2 || name.length > 100) {
        e.contractName = "Contract name must be between 2 and 100 characters";
      } else if (!/^[a-zA-Z0-9 \-_&./()]+$/.test(name)) {
        e.contractName = "Only letters, numbers, spaces, and - _ & . / ( ) are allowed";
      }
      if (contractDescription && contractDescription.length > 1000) {
        e.contractDescription = "Description must not exceed 1000 characters";
      }
    }
    if (idx === 1) {
      approvers.forEach((a, i) => {
        const name = a.name.trim();
        if (!name) {
          e[`approver_${i}_name`] = "Name is required";
        } else if (name.length < 2 || name.length > 100) {
          e[`approver_${i}_name`] = "Name must be between 2 and 100 characters";
        } else if (!/^[a-zA-Z .'\\-]+$/.test(name)) {
          e[`approver_${i}_name`] = "Only letters, spaces, and . - ' are allowed";
        }
        if (empty(a.email)) e[`approver_${i}_email`] = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) e[`approver_${i}_email`] = "Invalid email";
      });
    }
    if (idx === 2) {
      if (terms) {
        const t = terms.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
        if (t.length > 0 && t.length < 10) e.terms = "Contract terms must be at least 10 characters";
        else if (t.length > 5000) e.terms = "Contract terms must not exceed 5000 characters";
      }
      if (releaseType === "on_approval") {
        if (approvers.length > 0 && releaseApprovers.length === 0) {
          e.releaseApprovers = "At least one approver must be selected";
        }
      } else if (releaseType === "date") {
        if (empty(releaseDate)) e.releaseDate = "Release date is required";
      } else if (releaseType === "milestone") {
        if (!conditions.length) e.conditions = "At least one milestone is required";
        conditions.forEach((c, i) => {
          const label = c.label.trim();
          if (!label) {
            e[`cond_${i}_label`] = "Title is required";
          } else if (label.length < 2 || label.length > 100) {
            e[`cond_${i}_label`] = "Title must be between 2 and 100 characters";
          } else if (!/^[a-zA-Z0-9 \-_&./]+$/.test(label)) {
            e[`cond_${i}_label`] = "Only letters, numbers, spaces, and - _ & . / are allowed";
          }
          const pctStr = String(c.percentage || "").trim();
          const pct = parseFloat(pctStr);
          if (!pctStr || isNaN(pct)) {
            e[`cond_${i}_pct`] = "Percentage is required";
          } else if (!/^\d+$/.test(pctStr)) {
            e[`cond_${i}_pct`] = "Percentage must be a whole number (no decimals)";
          } else if (pct < 1 || pct > 100) {
            e[`cond_${i}_pct`] = "Enter a percentage between 1 and 100";
          }
          const deliverables = c.deliverables.trim();
          if (!deliverables) {
            e[`cond_${i}_deliverables`] = "Deliverables are required";
          } else if (deliverables.length < 5) {
            e[`cond_${i}_deliverables`] = "Deliverables must be at least 5 characters";
          } else if (deliverables.length > 1000) {
            e[`cond_${i}_deliverables`] = "Deliverables must not exceed 1000 characters";
          }
          if (empty(c.completionDate)) e[`cond_${i}_completionDate`] = "Completion date is required";
          if (c.milestoneReleaseType === "on_approval" && approvers.length > 0 && (c.milestoneApprovers || []).length === 0) {
            e[`cond_${i}_milestoneApprovers`] = "At least one approver must be selected";
          }
        });
      }
    }
    if (idx === 3) {
      payees.forEach((p, i) => {
        const name = p.name.trim();
        if (!name) {
          e[`payee_${i}_name`] = "Name is required";
        } else if (name.length < 2 || name.length > 100) {
          e[`payee_${i}_name`] = "Name must be between 2 and 100 characters";
        } else if (!/^[a-zA-Z .'\\-]+$/.test(name)) {
          e[`payee_${i}_name`] = "Only letters, spaces, and . - ' are allowed";
        }
        if (empty(p.email)) e[`payee_${i}_email`] = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) e[`payee_${i}_email`] = "Invalid email";
        if (p.wallet && p.wallet.trim()) {
          if (!isValidWalletAddress(p.wallet, currency)) {
            e[`payee_${i}_wallet`] = "Invalid wallet address for the selected currency";
          }
        }
        const pctStr = String(p.percentage || "").trim();
        const pct = parseFloat(pctStr);
        if (!pctStr) {
          e[`payee_${i}_amount`] = "Allocated percentage is required";
        } else if (isNaN(pct)) {
          e[`payee_${i}_amount`] = "Allocated percentage must be a valid number";
        } else if (!/^\d+$/.test(pctStr)) {
          e[`payee_${i}_amount`] = "Percentage must be a whole number (no decimals)";
        } else if (pct < 1 || pct > 100) {
          e[`payee_${i}_amount`] = "Percentage must be between 1 and 100";
        }
      });
      const totalPct = payees.reduce((sum, p) => sum + (parseFloat(p.percentage) || 0), 0);
      if (totalPct !== 100) {
        e.payeeTotalPct = `Total allocated percentage must equal 100% (currently ${totalPct}%)`;
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    // Step 4 is the last step (Review) — call API then redirect
    if (step === 4) {
      callCreateContractAPI("CREATE_CONTRACT");
      return;
    }
    setStep((s) => s + 1);
    setStepCount((c) => c + 1);
  };
  const handleBack = () => {
    setErrors({});
    setStep((s) => s - 1);
    setStepCount((c) => c - 1);
  };

  const buildPayload = (status) => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");

    const validApprovers = approvers.filter((a) => a.name.trim() && a.email.trim());
    const validPayees = payees.filter((p) => p.name.trim() && p.email.trim());

    const isReleaseConfigSet =
      (releaseType === "on_approval" && releaseApprovers.length > 0) ||
      (releaseType === "date" && releaseDate) ||
      (releaseType === "milestone" && conditions.some((c) => c.label.trim()));

    return {
      action: escrowAction,
      contractNo: contractNo,
      step: stepCount,
      merchantId,
      createdBy: merchantId,
      status,
      contractName,
      contractDescription,
      approvers: validApprovers.map((a) => ({ role: a.role, name: a.name, email: a.email })),
      approvalConfig: validApprovers.length > 0 ? approvalConfigs[0] : null,
      releaseConfig: isReleaseConfigSet ? releaseConfigs[0] : null,
      payees: validPayees.map((p) => ({ name: p.name, email: p.email, wallet: p.wallet, amount: parseFloat(p.percentage) || 0 })),
      payers: [],
      termsAndConditions: terms ? terms.trim() : "",
    };
  };

  const uploadMilestoneDocuments = async (contractId) => {
    if (releaseType !== "milestone") return;
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");

    const uploads = [];
    conditions.forEach((cond, i) => {
      if (!cond.documents || cond.documents.length === 0) return;
      cond.documents.forEach((file) => {
        const fd = new FormData();
        fd.append("contractId", contractId);
        fd.append("sequenceNo", i + 1);
        fd.append("file", file);
        fd.append("uploadedBy", merchantId);
        uploads.push(
          fetch(`${WEBSERVICE}/escrow/upload-milestone-document`, {
            method: "POST",
            headers: {
              Authorization: "bearer " + getAuthToken(),
              UUID: getAuthUuid(),
            },
            body: fd,
          })
        );
      });
    });

    if (uploads.length === 0) return;
    await Promise.all(uploads);
  };

  const callCreateContractAPI = async (status) => {
    if (!contractName.trim()) {
      setErrors((prev) => ({ ...prev, contractName: "Contract name is required" }));
      setStep(0);
      showToast("error", "Please provide a contract name before saving.");
      return;
    }
    setApiLoading(true);
    try {
      const payload = buildPayload(status);
      const response = await fetch(`${WEBSERVICE}/escrow/create-contract`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + getAuthToken(),
          UUID: getAuthUuid(),
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        showToast("error", "Unauthorized. Please log in and try again.");
        return;
      }
      if (response.status === 403) {
        showToast("error", "Access denied. You do not have permission to perform this action.");
        return;
      }
      if (response.status === 400) {
        let msg = "Invalid request. Please check your inputs and try again.";
        try { const d = await response.json(); if (d.message) msg = d.message; } catch (_) {}
        showToast("error", msg);
        return;
      }
      if (response.status === 500) {
        showToast("error", "Server error. Please try again later.");
        return;
      }
      if (!response.ok) {
        showToast("error", `Unexpected error (HTTP ${response.status}). Please try again.`);
        return;
      }

      const data = await response.json();
      if (data.status === true) {
        const responseData = Array.isArray(data.data) ? data.data[0] : data.data;
        const contractId = responseData?.contractId || responseData?.contractNo;
        if (responseData?.contractNo) {
          setContractNo(responseData.contractNo);
          setEscrowAction("UPDATE");
        }
        if (contractId) {
          await uploadMilestoneDocuments(contractId);
        }
        showToast("success", status === "SAVE_DRAFT" ? "Contract saved as draft" : "Escrow Contract created successfully");
        history.push("/escrow/payer/contracts");
      } else {
        showToast("error", data.message || "Something went wrong. Please try again.");
      }
    } catch (e) {
      showToast("error", "Network error. Please check your connection and try again.");
    } finally {
      setApiLoading(false);
    }
  };

  return (
    <Fragment>
      <style>{CSS}</style>
      <div className="container-scroller">
        <Sidebar /><Header />
        <div className="main-panel">
          <div className="ecc-page ecc-view-in">

            {/* Back */}
            <button className="ecc-back-link" onClick={() => history.goBack()}>
              <span className="ecc-back-arrow"><i className="fas fa-arrow-left" /></span>
              Back
            </button>

            {/* Header */}
            <div className="ecc-hdr">
              <h1 className="ecc-title">Full Escrow Contract Builder</h1>
              <p className="ecc-sub">Define all parties, conditions, and fund the escrow in one flow.</p>
            </div>

            {/* Stepper */}
            <div className="ecc-stepper">
              {STEPS.map((s, i) => (
                <div className="ecc-step-wrap" key={s.key}>
                  <div className={`ecc-step${step === i ? " active" : ""}${step > i ? " completed" : ""}`}>
                    <div className="ecc-step-circle">
                      {step > i
                        ? <i className="fas fa-check" style={{ fontSize: 12 }} />
                        : <i className={`fas ${s.icon}`} style={{ fontSize: 13 }} />}
                    </div>
                    <span className="ecc-step-label">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`ecc-step-connector${step > i ? " done" : ""}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step panels */}
            {step === 0 && (
              <StepDetails
                contractName={contractName} setContractName={setContractName}
                contractDescription={contractDescription} setContractDescription={setContractDescription}
                errors={errors}
              />
            )}
            {step === 1 && (
              <StepApprovers
                approvers={approvers} setApprovers={setApprovers}
                approvalType={approvalType} setApprovalType={setApprovalType}
                errors={errors}
              />
            )}
            {step === 2 && (
              <StepConditions
                conditions={conditions} setConditions={setConditions}
                approvers={approvers}
                releaseApprovers={releaseApprovers} setReleaseApprovers={setReleaseApprovers}
                releaseType={releaseType} setReleaseType={setReleaseType}
                releaseDate={releaseDate} setReleaseDate={setReleaseDate}
                terms={terms} setTerms={setTerms}
                errors={errors}
              />
            )}
            {step === 3 && (
              <StepPayees
                payees={payees} setPayees={setPayees}
                currency={currency} totalAmount={totalAmount}
                errors={errors}
              />
            )}
            {step === 4 && (
              <StepReview
                contractName={contractName} contractDescription={contractDescription}
                approvers={approvers} approvalType={approvalType}
                releaseApprovers={releaseApprovers}
                conditions={conditions} payees={payees}
                currency={currency} terms={terms}
                releaseType={releaseType} releaseDate={releaseDate}
              />
            )}
            {/* Footer navigation */}
            <div className="ecc-footer">
              <button
                className="ecc-btn-back"
                onClick={step === 0 ? () => history.goBack() : handleBack}
              >
                <i className="fas fa-arrow-left" style={{ fontSize: 12 }} />
                {step === 0 ? "Cancel" : "Back"}
              </button>

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {step < 4 && (
                  <button
                    className="ecc-btn-draft"
                    disabled={apiLoading}
                    onClick={() => callCreateContractAPI("SAVE_DRAFT")}
                  >
                    {apiLoading
                      ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Saving…</>
                      : <><i className="fas fa-save" style={{ fontSize: 12 }} /> Save as Draft</>}
                  </button>
                )}
                {step < 4 ? (
                  <button className="ecc-btn-next" onClick={handleNext} disabled={apiLoading}>
                    Continue
                    <i className="fas fa-arrow-right" style={{ fontSize: 12 }} />
                  </button>
                ) : (
                  <button className="ecc-btn-next" onClick={handleNext} disabled={apiLoading}>
                    {apiLoading
                      ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Creating…</>
                      : <><i className="fas fa-file-signature" style={{ fontSize: 12 }} /> Create Escrow</>}
                  </button>
                )}
              </div>
            </div>

          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
